/**
 * InstanceDetailDrawer — multi-mode drawer covering the full lifecycle
 * of a workflow instance.
 *
 * Per plan §"Workflow runtime lifecycle" + §Gap 3:
 *   * `pre_launch` — no instance_id yet. Renders a SchemaForm against
 *     the catalog `input_schema` with optional pre-fill from analyze_email.
 *     "Launch" button generates `idempotency_key` ONLY on submit and
 *     calls orchestratorApi.triggerWorkflow.
 *   * `live` — running / paused_hitl. PhasePipeline + status pill +
 *     correlation_id copy + Cancel button (with confirmation dialog).
 *   * `terminal` — completed / failed / cancelled. PhasePipeline frozen
 *     + final-state summary + WorkflowArtifactsSection (with click-through
 *     to the shared ArtifactLightbox). For endorsement workflows,
 *     CarrierHandoff + EndorsementHistory render. On failure, Retry
 *     button creates a new instance with `idempotency_key = previous +
 *     "-retry-N"`.
 *
 * Mounted in the shared <PaneTakeoverDrawer> (bottom-anchored, full frame —
 * the task pane is too narrow for right-anchored), which every full-frame
 * takeover in the pane uses so their entrances cannot drift apart.
 *
 * SchemaForm and HitlResponseDrawer are owned by Agent B. Until those
 * land we render a minimal JSON textarea fallback inside the drawer so
 * the drawer ships standalone; Agent B replaces the fallback by
 * supplying the SchemaForm component via props (kept open via the
 * `renderInputForm` injection) — no consumer-side churn.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import {
  findPolicyOption,
  readPolicyOptions,
  readPolicyTermKey,
} from "./policyPrefillOptions";
import { OversightLevelSelector } from "@product-app/common/workflows/launch/OversightLevelSelector";
import {
  DEFAULT_OVERSIGHT_LEVEL,
  comfortLabel,
  type OversightLevel,
} from "@product-app/common/workflows/oversightLevels";
import { shouldShowOversight } from "@product-app/common/workflows/shouldShowOversight";
import { shouldShowSpToggle, usesSpPool } from "@product-app/common/workflows/shouldShowSpToggle";
import { fetchSpCredsReady } from "../features/spCredsReadiness";
import { fetchSpPoolReadiness, spPoolPermitsLaunch } from "./SpPoolReadiness";
import {
  buildLaunchIdentityInputs,
  launchRequiresMessageIdentity,
} from "@product-app/common/workflows/launch/launchPayload";
import { prefillSatisfiesRequired } from "@product-app/common/workflows/schemaHelpers";
import {
  buildLaunchBinding,
  mergeLaunchSlice,
  splitLaunchValues,
} from "@product-app/common/workflows/launch/launchSchemaBinding";
// Plan glittery-stargazing-treehouse Phase 3 — the shared launch core both
// the add-in and ora call, so a launch-time fix lands once. See each
// module's own docstring for what it replaces here.
import { resolveLaunchTerm } from "@product-app/common/workflows/launch/core/resolveLaunchTerm";
import { assembleLaunchInputs } from "@product-app/common/workflows/launch/core/assembleLaunchInputs";
import { buildLaunchMetadata } from "@product-app/common/workflows/launch/core/buildLaunchMetadata";
import {
  isEndorsementFamily,
  requiresClient,
  requiresGraphMessageId,
} from "@product-app/common/workflows/launch/core/launchRules";
import {
  STATUS_LABELS,
  statusDisplayLabel,
  stepDisplayName,
} from "@product-app/common/workflows/statusLabels";
import { isWaitingOnCarrierDocuments } from "@product-app/common/workflows/statusPill";
import {
  clearDispositionLabel,
  extractEarlyExitReason,
  extractManualClear,
  extractResultWarnings,
  warningsFromStatus,
} from "@product-app/common/workflows/resultWarnings";
import { WorkflowResultSection } from "@product-app/common/workflows/components/WorkflowResultSection";
import { FormattedJsonView } from "@product-app/common/workflows/components/FormattedJsonView";
import { extractEmbeddedJson } from "@product-app/common/workflows/extractEmbeddedJson";
import { WorkflowHitlHistorySection } from "@product-app/common/workflows/components/WorkflowHitlHistorySection";
import type { HitlEvent } from "@product-app/common/workflows/components/WorkflowHitlHistorySection";
import { DrawerFooter } from "@product-app/common/workflows/drawer/DrawerFooter";
import { InstanceField } from "@product-app/common/workflows/components/InstanceField";
import type { WorkflowResultBody } from "@product-app/common/workflows/result.types";
import {
  computeDurationFromDates,
  formatAbsolute,
  formatRelativeTime,
  formatTime,
} from "@product-app/common/workflows/formatTime";
import { RunSection } from "@product-app/common/workflows/drawer/sections/RunSection";
import type { RunRecordBodyProps } from "@product-app/common/workflows/runRecord/RunRecordBody";
import type { RunEvidenceStepsInput } from "@product-app/common/workflows/runRecord/RunEvidenceSection";
import type { WorkflowStatus } from "@product-app/features/workflows/monitor.types";
import {
  titleCaseId,
  readClientContext,
} from "@product-app/common/workflows/runRecord/runRecordInputs";
import { displayWorkflowVersion } from "@product-app/common/workflows/displayWorkflowVersion";
import { derivePhaseStatusesFromInstance } from "./PhasePipeline";
import WorkflowStepTooltipBubble from "@product-app/common/workflows/components/WorkflowStepTooltipBubble";
import CustomTooltip from "@product-app/common/components/CustomTooltip";
import { WorkflowArtifactsSection } from "@product-app/common/workflows/components/WorkflowArtifactsSection";
import { useArtifactLightbox } from "@product-app/common/workflows/components/WorkflowArtifactsGallery";
// Shared drawer primitives — keep ora and the plugin in visual parity
import { PinnedHeader } from "@product-app/common/workflows/drawer/PinnedHeader";
import { SourceRequestRow } from "./SourceRequestRow";
import { HitlDrawerBody } from "@product-app/common/workflows/drawer/bodies/HitlDrawerBody";
import { ActiveDrawerBody } from "@product-app/common/workflows/drawer/bodies/ActiveDrawerBody";
import { PendingEmailSendDrawerBody } from "@product-app/common/workflows/drawer/bodies/PendingEmailSendDrawerBody";
import SentReplySweepAction from "./SentReplySweepAction";
import { PendingEdocsDrawerBody } from "@product-app/common/workflows/drawer/bodies/PendingEdocsDrawerBody";
import { PhaseMilestoneStream } from "@product-app/common/workflows/drawer/sections/PhaseMilestoneStream";
import {
  buildPhaseStream,
  carrierEvidenceCandidateFor,
  liveCarrierEvidenceStepId,
  shouldShowCarrierEvidenceCard,
  readEvidenceDecision,
} from "@product-app/common/workflows/phaseModel";
import { CarrierEvidencePanel } from "@product-app/common/workflows/components/CarrierEvidencePanel";
import { SubmittedRequestSection } from "@product-app/common/workflows/drawer/sections/SubmittedRequestSection";
import { readSubmittedRequest } from "@product-app/common/workflows/submittedRequest";
import { isActivelyWorkingStatus } from "@product-app/common/workflows/sectionOrder";
import {
  buildPhaseDefinitions,
  resolveStepCatalogEntry,
} from "@product-app/common/workflows/drawer/sections/runStepsInputs";
import { TooltipRowList } from "@product-app/common/workflows/components/TooltipRowList";
import { DebugActionsPanel } from "@product-app/common/workflows/drawer/DebugActionsPanel";
import { useStepViewsFlag } from "../features/stepViewsFlag";
import { withoutHostDrawnSignIn } from "../features/hostDrawnSignIn";
import {
  BriefApproveView,
  CarrierPacketView,
  DoneView,
  ExceptionView,
  ReplyReadyView,
  RunningView,
  buildStepViewModel,
  hasStepViews,
  readCatalogSections,
  sectionLabelFor,
  stepViewFor,
} from "@product-app/common/workflows/steps";
import { openMessageInOutlook } from "../outlook/openMessage";
import { emitTouchpoint } from "../obs/touchpoints";
import { edocCheckMessage } from "@product-app/common/workflows/edocCheckMessage";
import { CompletedDrawerBody } from "@product-app/common/workflows/drawer/bodies/CompletedDrawerBody";
import { TerminalDrawerBody } from "@product-app/common/workflows/drawer/bodies/TerminalDrawerBody";
import type { DrawerSectionMap } from "@product-app/common/workflows/drawer/bodies/types";
import { pickDrawerStatusFamily } from "@product-app/common/workflows/drawer/pickDrawerStatusFamily";
import { useDebouncedFamily } from "@product-app/common/workflows/drawer/useDebouncedFamily";
import { useHitlSubmitLatch } from "@product-app/common/workflows/useHitlSubmitLatch";
import { PaneTakeoverDrawer } from "@product-app/common/workflows/components/PaneTakeoverDrawer";
import { WorkflowCenterBar } from "@product-app/common/workflows/components/WorkflowCenterBar";
import { MoreInformationOwnBackRowContext } from "@product-app/common/workflows/drawer/sections/MoreInformationSection";
import { TakeoverTitleHeader } from "@product-app/common/workflows/components/TakeoverTitleHeader";
import { workflowDisplayName } from "@product-app/common/workflows/workflowDisplayName";
import { TYPE_BODY, TYPE_TITLE } from "@product-app/common/workflows/paneTypography";
import { oraLogoUrl } from "../assetUrl";
import {
  classifyResumeAck,
  classifyResumeFailure,
  type DecisionAnswerOutcome,
} from "@product-app/common/workflows/decisionAnswerOutcome";
import CarrierHandoff from "./CarrierHandoff";
import EndorsementHistory from "./EndorsementHistory";
import { DecisionCard } from "@product-app/common/workflows/components/DecisionCard";
import { useAnsweredElsewhere } from "@product-app/common/workflows/useAnsweredElsewhere";
import { decisionRefusedBody } from "@product-app/common/workflows/copy";
import { type SideActionResult } from "@product-app/common/workflows/components/HitlSideActions";
import SchemaForm from "./SchemaForm";
import { PolicyPrefillSelector } from "./PolicyPrefillSelector";
import Skeleton from "./Skeleton";
import {
  ApiError,
  orchestratorApi,
  type ArtifactDto,
} from "../services/orchestratorApi";
import { findBlockingInstanceForEmail } from "../services/findRunningInstanceForEmail";
import { useFullCatalogEntry, type FullCatalogEntry } from "../hooks/useFullCatalogEntry";
import { CatalogEntryNotice } from "./CatalogEntryNotice";
import type {
  ActiveWorkflow,
  HitlQuestion,
  InstanceStatus,
  PhaseDefinition,
  WorkflowCatalogEntry,
} from "../types/workflow";
import type { ClientDetails } from "../types/client";
import { useInstancePush } from "@product-app/common/workflows/useInstancePush";
import { useWarningAcknowledgements, type WarningAckState } from "@product-app/common/workflows/hooks/useWarningAcknowledgements";
import { WarningAcknowledgementList } from "@product-app/common/workflows/components/WarningAcknowledgementList";
import { isLaunchableInPlugin } from "../features/pluginCatalogExclusions";
import {
  EARLY_EXIT_WARNING_ID,
  doneSentenceBesideLines,
  pillNeedsAcknowledgement,
  readWarningItems,
  warningMarker,
  withAcknowledgements,
} from "@product-app/common/workflows/warningAcknowledgement";
import { logger } from "../obs/logger";

/** The running view's auto-close budget, shared by the countdown the Servicer
 *  sees and the timer that actually dismisses the drawer.
 *
 *  TEN, not five: five was short enough that a slow first paint could spend the
 *  whole budget, and the Servicer watched the view leave without ever having
 *  read it. The countdown is a courtesy on a run that needs nothing, so it can
 *  afford to be generous; what it must never be is faster than the data. */
const STEP_VIEW_AUTO_CLOSE_SECONDS = 10;

function titleCase(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export type DrawerMode = "pre_launch" | "live" | "terminal";

export interface InstancePushSnapshot {
  status: InstanceStatus | null;
  loading: boolean;
  isStale: boolean;
  lastError: Error | null;
}

export interface InstanceDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Where the running view's countdown lands, when it is not held.
   *
   *  SEPARATE FROM `onClose` on purpose. A manual close should leave the
   *  Servicer where they were — if they opened this from the Launch tab,
   *  yanking them to the queue would be the pane deciding where they are.
   *  The countdown is the opposite case: nobody is watching, so it lands on
   *  the pane's landing page (the Needs You queue, story pinned-pane AC-2),
   *  which is what the announced copy promises. Falls back to `onClose`. */
  onAutoClose?: () => void;
  /**
   * Leave the drawer and go back to where the Servicer came from.
   *
   * SEPARATE FROM `onClose` in INTENT even though a host will usually wire
   * both to the same handler: this one is navigation and SAYS WHERE IT GOES
   * via `backLabel`. The drawer is `height: 100vh`, so while it is open the
   * pane's own tab bar is covered. It is the only visible exit (there is no
   * Close button); Esc does the same. Absent → the single back row still
   * renders, labelled "Back", and calls `onClose`.
   */
  onBack?: () => void;
  /**
   * The DESTINATION, in the host's words — "Back to this email", "Back to
   * your queue". The drawer deliberately does not infer it: only the host
   * knows whether this was opened from the context strip, the catalog or a
   * queue row, and a control that guesses wrong is worse than a generic one.
   * Ignored without `onBack` (the row then reads "Back").
   */
  backLabel?: string;
  mode: DrawerMode;
  /**
   * Catalog entry for the workflow being LAUNCHED — the one the caller picked
   * (a catalog tile, a Needs You row, a reuse). Every launch gate reads this
   * one, so it keeps its exact meaning: absent means "no workflow chosen".
   *
   * It is NOT the entry that describes a run being VIEWED. Two open paths (the
   * ora-notification landing and `Action.InvokeAddInCommand`) carry an
   * instance and no workflow id at all, so this is legitimately absent there —
   * see `displayCatalogEntry` below, which resolves from the run itself.
   *
   * May be a COMPACT list entry (plugin catalog round, 2026-10-06). The drawer
   * reads the full entry (useFullCatalogEntry) before any reader below sees it.
   */
  catalogEntry?: WorkflowCatalogEntry | null;
  /**
   * The full workflow catalog. Needed because the entry that DECLARES a run's
   * steps has to be resolved from the run's own `workflow_id`: without it the
   * shared Steps log gets no declared manifest and degrades to "every row the
   * server reported, all of them green", hiding every step that never ran
   * (instance 81363a33-7ea0-46ff-9a1e-fd1e7ddc153c).
   *
   * Since 2026-10-06 this is the pane's COMPACT list: used only to find the
   * run's workflow, whose full entry is then read. Never handed to RunStepsLog.
   */
  catalog?: readonly WorkflowCatalogEntry[] | null;
  /** Phase manifest (richer than catalog phase_count). Optional. */
  phases?: PhaseDefinition[];

  // ─── pre_launch mode props ─────────────────────────────────────────
  /** Pre-fill values seeded from analyze_email suggestion. */
  prefill?: Record<string, unknown>;
  /** Per-field confidence levels from analyze_email extraction, keyed by schema property name. */
  fieldConfidence?: Record<string, "high" | "medium" | "low">;
  /** When true, a background prefill call is in flight; auto-launch waits until it resolves. */
  prefillLoading?: boolean;
  /**
   * Outlook message id (``Office.context.mailbox.item.itemId``) of the email
   * the workflow is being launched from. Threaded into the launch ``inputs``
   * as the hidden ``graph_message_id`` silent-capture field so the substrate
   * can ``createReply`` (e.g. carrier_mortgagee_update's reply draft). Without
   * this an email-originated run reaches the workflow with no message id and
   * the reply draft is silently skipped (regression: instance 44a7dc39).
   */
  graphMessageId?: string | null;
  /**
   * RFC 5322 Message-ID of the analyzed email. Threaded into launch metadata
   * (metadata.email.internet_message_id) so the substrate can close the
   * analyze-email feedback loop: per-field MLflow assessments (predicted vs
   * launched) + corrections persisted on the email_analysis cache row.
   */
  internetMessageId?: string | null;
  /** Called when triggerWorkflow succeeds. Parent transitions to `live`. */
  onLaunched?: (result: {
    instance_id: string;
    workflow_id: string;
    idempotency_key: string;
  }) => void;
  /**
   * Called when the substrate's concurrent-run guard REFUSED this launch and
   * the blocking run was identified (story pinned-pane, Monitoring DoD).
   *
   * Observation only — the drawer's own refusal UI (the concurrent-run confirm
   * dialog) is unchanged, and a host that passes nothing here sees byte-
   * identical behaviour. The pinned pane wires it so a re-run refusal started
   * from the context strip is logged with the blocking instance id, which is
   * the join key to the guard's server-side refusal log.
   */
  onLaunchRefused?: (refusal: {
    instance_id: string;
    reason_code: string;
  }) => void;
  /**
   * When true the drawer checks whether all required schema fields are present
   * in `prefill` and, if so, fires the launch immediately without showing the
   * input form. If fields are missing the form is shown pre-filled instead.
   */
  autoLaunch?: boolean;

  /**
   * Optional input form override. When supplied, this component owns the
   * input editing UI (Agent B's SchemaForm). When omitted we render a
   * minimal JSON textarea fallback so the drawer is functional standalone.
   * Receives the schema, current values, and a setter — must call
   * `onSubmit(values)` on its own submit affordance.
   */
  renderInputForm?: (args: {
    schema: Record<string, unknown> | null;
    values: Record<string, unknown>;
    /**
     * The drawer's OWN `useState` setter, so a form can pass a functional
     * update. The grouped launch form needs one: RE-ANALYSE's success drop of
     * `values[actionsKey]` settles AFTER the host has merged the fresh brief,
     * and a drop computed from a stale closure would throw that merge away.
     * A caller that only ever passes a plain record is unaffected — the wider
     * type accepts it.
     */
    setValues: Dispatch<SetStateAction<Record<string, unknown>>>;
    onSubmit: (next: Record<string, unknown>) => void;
    submitting: boolean;
    fieldConfidence?: Record<string, "high" | "medium" | "low">;
    fieldLoading?: Record<string, boolean>;
    /**
     * True while a background prefill is in flight. OPTIONAL, so every existing
     * stub that supplies `renderInputForm` keeps compiling and the flat
     * `SchemaForm` branch simply ignores it. The grouped form's create-rule
     * waits on it: a first-paint create against an empty action list freezes an
     * empty action that the drawer's later shallow merge cannot enter, and the
     * prefilled brief then paints at run level where no control reads it.
     */
    prefillLoading?: boolean;
    /**
     * The drawer-owned Epic sign-in fallback, handed to the form so it can sit
     * right above the Submit button (operator request 2026-08-03). The drawer
     * owns the state; the form only places the node — same division as
     * `renderClientSection`. Null whenever the default sign-in is fine (story
     * 28633): it renders only when the shared pool / dedicated SP is not
     * available ("Use my own Epic login" + Retry) or after that switch (Undo).
     */
    signInSection?: React.ReactNode;
    /**
     * Non-null = the launch is blocked by a precondition OUTSIDE the payload,
     * and this string says why. Pass straight through to JsonSchemaForm's
     * `submitDisabledReason` so the Submit button is actually greyed out.
     *
     * Do NOT fold this into `submitting`: that disables every control including
     * "Use my own Epic login", so an operator blocked by an empty SP pool could
     * not switch to their personal login to unblock themselves.
     */
    submitDisabledReason?: string | null;
    /** Client card (and the client-gate prompt) — scrolls with the grouped form. */
    leadingContent?: React.ReactNode;
    /** Oversight selector — placed in RUN OPTIONS by LaunchForm. */
    runLevelControls?: React.ReactNode;
  }) => React.ReactNode;

  /**
   * Combined prefill (analyze → auto-open): client confirmation/selection
   * section rendered ABOVE the input form in pre_launch mode. Render-prop
   * mirror of ``renderInputForm`` — the parent owns the client state
   * (useClientContext); the drawer only places the section.
   */
  renderClientSection?: () => React.ReactNode;

  // ─── live mode props ───────────────────────────────────────────────
  /** Active instance id for live + terminal modes. */
  instanceId?: string | null;
  /**
   * Client SK for the workflow context. Takes priority over
   * push.status.inputs.client_sk (which is absent for workflows whose
   * input schemas don't declare the field).
   */
  clientSk?: number | null;
  /** Client display name — included in metadata.client_context so the monitor grid shows it. */
  clientName?: string | null;
  /**
   * When provided, the drawer treats this snapshot as the source of
   * truth instead of running its own polling. Lets a parent share
   * push state across multiple consumers.
   */
  pushOverride?: InstancePushSnapshot;
  /** Called when the user confirms cancellation. */
  onCancelled?: (instanceId: string) => void;

  // ─── terminal mode props ───────────────────────────────────────────
  /**
   * Pre-fetched artifacts; when omitted the drawer fetches them itself
   * upon entering terminal mode.
   */
  artifacts?: ArtifactDto[];
  artifactsLoading?: boolean;
  /**
   * Idempotency key of the previous run, used for retry suffixing.
   * When the user clicks Retry we trigger a new instance with
   * `idempotency_key = previousKey + "-retry-" + attempt`.
   */
  previousIdempotencyKey?: string;
  /** Called when retry succeeds → parent switches to live mode w/ new id. */
  onRetried?: (result: {
    instance_id: string;
    workflow_id: string;
    idempotency_key: string;
  }) => void;

  // `onPolicyChosen` (declared/threaded, never passed by any caller since
  // 2026-07-21) removed here — plan glittery-stargazing-treehouse Phase 3
  // step 5. `PolicyPrefillSelector` (common/, frozen) still declares the
  // prop as optional, so simply not passing it is a no-op, not a behavior
  // change.
  /**
   * running-drawer AC-9: reuse OPENS a pre-filled launch surface and starts
   * nothing. The host receives exactly the values the AM read plus the RUN's
   * own email identity, so a later RE-ANALYSE (owned by `prefill-drawer`)
   * re-derives from the email the run was launched from — never from whatever
   * mail item happens to be selected now.
   */
  onReuse?: (values: Record<string, unknown>, identity: string | null) => void;
  /**
   * Called the moment a HITL response is submitted successfully — BEFORE the
   * drawer closes. The pinned pane's "Needs you" queue lives behind this
   * drawer and does not re-derive its own rows (design Constraint 19), so it
   * has no other way to learn that the row it is showing was just answered:
   * SignalR does not reliably fire a push event for this transition (see the
   * `ownPush.refetch` burst-poll below, added for the same reason on the
   * drawer's own status), and the queue's poll floor is 30-60s. Optional so
   * a host with no queue to nudge (or a test) is unaffected.
   */
  onHitlResponded?: () => void;
}

/**
 * The guard's own reason code, for the pinned pane's refusal log.
 *
 * The product-API bridge forwards the substrate's `code` alongside the
 * message, so prefer it; fall back to the HTTP status. NEVER the message —
 * that carries the blocking run's owner display name, which is gated on the
 * visibility waterfall and must not reach a log line.
 */
export function refusalReasonCode(err: unknown): string {
  const body = (err as { response_body?: unknown })?.response_body;
  const code = (body as { code?: unknown } | null | undefined)?.code;
  if (typeof code === "string" && code.trim().length > 0) return code.trim();
  if (typeof code === "number") return String(code);
  const status = (err as { status?: unknown })?.status;
  return typeof status === "number" ? `http_${status}` : "conflict";
}

function genIdempotencyKey(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  // Fallback: unguessable enough for stub mode.
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// `withTermIdentity` (the blank-only term-identity backfill onto the action)
// moved to `@product-app/common/workflows/launch/core/assembleLaunchInputs.ts`
// (plan glittery-stargazing-treehouse Phase 3 step 3) — it is now internal to
// `assembleLaunchInputs`, which both `handleLaunch` and `handleRetry` call.

const STATUS_PILL: Record<
  string,
  { bg: string; text: string; label: string }
> = {
  running: {
    bg: "bg-sunrise/20 border border-primary-warning-200",
    text: "text-primary-warning-700",
    label: STATUS_LABELS.running,
  },
  paused_hitl: {
    bg: "bg-primary-warning-50 border border-primary-warning-200",
    text: "text-primary-warning-700",
    label: STATUS_LABELS.paused_hitl,
  },
  warm_hitl: {
    bg: "bg-primary-warning-50 border border-primary-warning-200",
    text: "text-primary-warning-700",
    label: STATUS_LABELS.warm_hitl,
  },
  rpa_review: {
    bg: "bg-primary-warning-50 border border-primary-warning-200",
    text: "text-primary-warning-700",
    label: STATUS_LABELS.warm_hitl,
  },
  // Non-terminal parked states — neutral white pills, their pre-brand-sweep
  // look (the old primary-blue-* classes never resolved to CSS, so these
  // always rendered neutral). Deliberately NOT the purple brand tone.
  pending_email_send: {
    bg: "bg-base-white border border-primary-gray-light-200",
    text: "text-primary-gray-light-700",
    label: STATUS_LABELS.pending_email_send,
  },
  pending_edocs: {
    bg: "bg-base-white border border-primary-gray-light-200",
    text: "text-primary-gray-light-700",
    label: STATUS_LABELS.pending_edocs,
  },
  completed: {
    bg: "bg-primary-success-50 border border-primary-success-200",
    text: "text-primary-success-700",
    label: STATUS_LABELS.completed,
  },
  failed: {
    bg: "bg-primary-error-50 border border-primary-error-200",
    text: "text-primary-error-700",
    label: STATUS_LABELS.failed,
  },
  cancelled: {
    bg: "bg-primary-gray-light-100 border border-primary-gray-light-200",
    text: "text-primary-gray-light-600",
    label: STATUS_LABELS.cancelled,
  },
  timeout_cancelled: {
    bg: "bg-primary-gray-light-100 border border-primary-gray-light-200",
    text: "text-primary-gray-light-600",
    label: STATUS_LABELS.timeout_cancelled,
  },
  // R55 (2026-10-06): a rejected run keeps its own label — it used to fall
  // through to the raw "rejected".
  rejected: {
    bg: "bg-primary-gray-light-100 border border-primary-gray-light-200",
    text: "text-primary-gray-light-600",
    label: STATUS_LABELS.rejected,
  },
};

function statusPill(status: string | null | undefined) {
  if (!status) return null;
  const cfg = STATUS_PILL[status.toLowerCase()];
  if (!cfg)
    return {
      bg: "bg-primary-gray-light-100 border border-primary-gray-light-200",
      text: "text-primary-gray-light-600",
      label: status.replace(/_/g, " "),
    };
  return cfg;
}

/** An API that predates acknowledgement: does the status itself say there is something to look at? */
function statusCarriesWarnings(status: InstanceStatus | null | undefined): boolean {
  const legacy = status?.metadata?.warning;
  return (
    // M1: every non-completed terminal run carries ended_early; only a COMPLETED
    // run that ended early is a warning (the body's earlyExitReason is completed-only).
    (status?.status?.toLowerCase() === "completed" && status?.ended_early === true) ||
    (status?.result_warnings?.length ?? 0) > 0 ||
    (typeof legacy === "string" && legacy !== "")
  );
}

/**
 * R89: which LEGACY warning texts the terminal body still renders for this
 * status, given the acknowledgement lines. The body's own stand-down rules, in
 * ONE place (the parent's pill and the body both call it, so they cannot drift):
 * each legacy source stands down only when the list carries its own line.
 */
/**
 * The persisted result body for a terminal run (GET /result), fetched once per
 * instance by the PARENT so the header pill and the record read the same data.
 * Keyed by instance: a different run never shows the previous run's body.
 */
function useRunResultBody(instanceId: string | null, enabled: boolean): WorkflowResultBody | null {
  const [fetched, setFetched] = useState<{ id: string; body: WorkflowResultBody | null } | null>(null);
  const fetchedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!instanceId || !enabled) return;
    if (fetchedFor.current === instanceId) return;
    fetchedFor.current = instanceId;
    const set = (body: WorkflowResultBody | null) => setFetched({ id: instanceId, body });
    void orchestratorApi
      .getWorkflowResult(instanceId)
      .then((r) => {
        const result = r.result;
        set(result && typeof result === "object" && Object.keys(result).length > 0 ? (result as WorkflowResultBody) : null);
      })
      .catch(() => set(null));
  }, [instanceId, enabled]);
  return fetched && fetched.id === instanceId ? fetched.body : null;
}

/**
 * The "finished elsewhere" notice. Local to the pane (the shared banner is
 * ora's, with a Close button and no warning tone): while acknowledgement is
 * open the run reads "Completed*" in amber, so this notice wears the same
 * amber instead of a green that contradicts the pill, and its button is
 * "Dismiss" -- the pane has no Close.
 */
function FinishedElsewhereBanner({
  status,
  warning,
  onDismiss,
}: {
  status: "completed" | "cancelled" | "timeout_cancelled" | "failed";
  warning: boolean;
  onDismiss: () => void;
}) {
  const verb =
    status === "completed" ? "completed" : status === "failed" ? "failed" : "was cancelled";
  const tone =
    status === "completed" && !warning
      ? "bg-primary-success-50 border-primary-success-300 text-primary-success-800"
      : status === "failed"
        ? "bg-primary-error-50 border-primary-error-300 text-primary-error-800"
        : "bg-primary-warning-50 border-primary-warning-300 text-primary-warning-800";
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="workflow-finished-elsewhere-banner"
      className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-xs ${tone}`}
    >
      <span className="font-medium">This workflow {verb} elsewhere.</span>
      <button
        type="button"
        onClick={onDismiss}
        className="rounded-md border border-current bg-base-white/60 px-2 py-0.5 text-[11px] font-semibold hover:bg-base-white transition-colors duration-150"
        data-testid="workflow-finished-elsewhere-banner-close"
      >
        Dismiss
      </button>
    </div>
  );
}

function legacyWarningsShown(
  status: InstanceStatus | null | undefined,
  items: readonly { id: string; kind: string }[] | null | undefined,
  resultData?: Record<string, unknown> | null,
) {
  const lines = items ?? [];
  const has = (id: string) => lines.some((i) => i.id === id);
  const completed = (status?.status ?? "").toLowerCase() === "completed";
  const md = status?.metadata?.warning;
  const early =
    completed &&
    (extractEarlyExitReason(status as Record<string, unknown> | null) ??
      extractEarlyExitReason(resultData ?? null)) !== null &&
    !has(EARLY_EXIT_WARNING_ID);
  const metadata = completed && typeof md === "string" && md !== "" && !has("metadata_warning");
  const result =
    completed &&
    (warningsFromStatus(status).length > 0 || extractResultWarnings(resultData ?? null).length > 0) &&
    !lines.some((i) => i.kind === "result_warning");
  return { early, metadata, result, any: early || metadata || result };
}

/**
 * The terminal header pill with the warning-acknowledgement marker (2026-10-06):
 * "Completed*" in amber while a warning line is open, plain green "Completed" once
 * every line is acknowledged. Any other status keeps its own label and colours
 * and only gains the "*" — a failed run is "Error*" then "Error", never
 * "Completed". `needsAcknowledgement` null (an API that predates the feature)
 * keeps today's colours and adds only the plain legacy "*" (ora's has_warnings
 * star) when the status itself carries a warning — never a clean "Completed"
 * for a run that says it has something to look at (ruling R63).
 */
function terminalStatusPill(
  status: string | null | undefined,
  needsAcknowledgement: boolean | null,
  legacyHasWarnings: boolean,
) {
  const base = statusPill(status);
  if (!base) return null;
  const marker = warningMarker({
    status,
    label: base.label,
    needsAcknowledgement,
    hasWarnings: legacyHasWarnings,
  });
  return {
    bg: marker.amber ? "bg-primary-warning-50 border border-primary-warning-200" : base.bg,
    text: marker.amber ? "text-primary-warning-700" : base.text,
    label: marker.asterisk ? `${base.label}*` : base.label,
    ariaLabel: marker.ariaLabel,
    title: marker.title,
  };
}

// `isEndorsementWorkflow` / `MORTGAGEE_UPDATE_WORKFLOWS` / `isMortgageeUpdateWorkflow`
// moved to `@product-app/common/workflows/launch/core/launchRules.ts` as
// `isEndorsementFamily` / `MORTGAGEE_UPDATE_WORKFLOWS` / `requiresClient` (plan
// glittery-stargazing-treehouse Phase 3 step 3) — one home shared with ora.

export function InstanceDetailDrawer(props: InstanceDetailDrawerProps) {
  const {
    open,
    onClose,
    onAutoClose,
    onBack,
    backLabel,
    mode,
    catalogEntry: catalogEntryProp,
    catalog,
    phases,
    prefill,
    fieldConfidence,
    prefillLoading,
    graphMessageId,
    internetMessageId,
    autoLaunch,
    onLaunched,
    onLaunchRefused,
    renderInputForm,
    renderClientSection,
    instanceId,
    clientSk: clientSkProp,
    clientName: clientNameProp,
    pushOverride,
    onCancelled,
    artifacts: artifactsProp,
    artifactsLoading: artifactsLoadingProp,
    previousIdempotencyKey,
    onRetried,
    onReuse,
    onHitlResponded: onHitlRespondedProp,
  } = props;

  // ── Push subscription (live + terminal modes) ─────────────────────
  // Only subscribe when we're actually in those modes to avoid pulling
  // status for an instance the user already closed.
  const ownPush = useInstancePush<InstanceStatus>(
    mode === "live" ? instanceId ?? null : null,
    {
      // A hidden pane polls every 5 minutes, the traffic round's rule.
      hiddenIntervalMs: 300_000,
      enabled: open && mode === "live" && !pushOverride,
      getStatus: (id) => orchestratorApi.getStatus(id),
      getPushChannel: (id) => orchestratorApi.getPushChannel(id),
      joinSignalRGroup: (id, connectionId, hub) =>
        orchestratorApi.joinSignalRGroup(id, connectionId, hub),
    },
  );
  // step-views' rollback lever (story 26739). Resolved HERE for exactly the
  // reason above: the add-in is two independent inner trees, and a live-only
  // resolution leaves every completed / failed / cancelled run on today's
  // body. Never read inside LiveBody or TerminalBody.
  const stepViewsFlag = useStepViewsFlag(
    `${open ? "open" : "closed"}:${instanceId ?? ""}`,
  );

  const push: InstancePushSnapshot = pushOverride ?? {
    status: ownPush.status,
    loading: ownPush.loading,
    isStale: ownPush.isStale,
    lastError: ownPush.lastError,
  };

  // ── The FULL catalog entries (plugin catalog round, 2026-10-06) ─────────
  // The pane's catalog list is COMPACT — names, no input_schema / steps /
  // manifest — so both entries this drawer reads are completed here, once per
  // workflow per pane session (catalogEntryCache). Every reader below sees a
  // FULL entry or null:
  //   * `catalogEntry` — the workflow being LAUNCHED (the prop, completed).
  //     Every launch gate, the form, auto-launch and retry read this name,
  //     unchanged.
  //   * `displayCatalogEntry` — the entry that describes the RUN (further down).
  // While a read is in flight the form and the step list are a loading line; a
  // failed read is an error with Try again. An entry without the compact
  // marker (an older API, the demo stub) is used as it is, with no read.
  const launchEntry = useFullCatalogEntry(catalogEntryProp ?? null);
  const catalogEntry = launchEntry.entry;
  // The plugin never STARTS Epic Bulk Policy Update / Epic Bulk Review / oraFix:
  // a Retry or Reuse of such a run opened here would start it from the pane.
  const runLaunchableInPlugin = isLaunchableInPlugin({
    id: push.status?.workflow_id ?? catalogEntry?.id ?? null,
  });
  // While there is no full launch entry (none, loading, failed) `catalogEntry` is
  // null and handleRetry would silently do nothing — so footer Retry is disabled
  // (with a reason) until the full entry lands.
  // The two notification landings carry an instance and no workflow id, so the
  // run's entry is found in the list by the run's own workflow_id, then
  // completed the same way. With a launch entry in hand, that one describes the
  // run (as before).
  const runSeed = catalogEntryProp
    ? null
    : resolveStepCatalogEntry(catalog, push.status?.workflow_id);
  const runOnlyEntry = useFullCatalogEntry(runSeed);
  const runEntry = catalogEntryProp ? launchEntry : runOnlyEntry;

  // The step views are the mortgagee updates' (story 26739): the flag turns them
  // on, and only for those workflows — never for every run. Same gate as ora's
  // drawer (`hasStepViews`, shared).
  const stepViewsEnabled =
    stepViewsFlag && hasStepViews(push.status?.workflow_id ?? catalogEntry?.id ?? null);

  // ── effectiveMode: auto-advances live → terminal when push reaches a
  //    terminal state, without requiring App.tsx to track completion.
  const effectiveMode: DrawerMode =
    mode === "live" && ownPush.isTerminal ? "terminal" : mode;

  // ── step views (story 26739) ──────────────────────────────────────
  // WHICH view, derived from `push.status` on EVERY render. Never from
  // `mode`, never from `useState`: the terminal push arrives as a status
  // change on this same mounted tree, and a view held in state would keep
  // showing `running` for a run that finished thirty seconds ago.
  //
  // The carrier-evidence gate is the same one the decision card uses
  // (`shouldShowCarrierEvidenceCard`) rather than a raw `readEvidenceDecision`:
  // the raw read stays non-null for the life of the run, so the ungated
  // version would put every COMPLETED mortgagee run on the carrier packet.
  // The RECORDED decision only. The decision CARD additionally falls back to
  // `placeholderCarrierPostsCard` so a v2 run always has something to show —
  // but that placeholder exists for every v2 run from launch, so using it here
  // would put every running mortgagee run on the carrier packet.
  const stepViewEvidenceCandidate = stepViewsEnabled
    ? readEvidenceDecision(push.status)
    : null;
  const stepViewEvidence =
    stepViewEvidenceCandidate &&
    shouldShowCarrierEvidenceCard(
      push.status?.status,
      liveCarrierEvidenceStepId(push.status),
      stepViewEvidenceCandidate,
    )
      ? stepViewEvidenceCandidate
      : null;
  const stepViewName = stepViewsEnabled
    ? stepViewFor({
        status: push.status?.status ?? null,
        hitlQuestion: push.status?.hitl_question ?? null,
        evidenceDecision: stepViewEvidence,
      })
    : null;

  // The running view's 5s auto-close. The COUNTDOWN — visible, announced,
  // holdable — belongs to the shared `RunningView`; the timer that acts on it
  // belongs here. Both are armed by the same condition and both are released
  // by Hold, so the digits on screen and the thing that actually happens can
  // never disagree.
  //
  // IT DISMISSES THE DRAWER, NEVER THE TASK PANE. Nothing here reaches for the
  // Office hop (`Office.addin.hide()` / `closeContainer()`) that hides the pane
  // itself; the hook that used to own that call is deleted.
  // A PINNED PANE MUST NOT BE CLOSED: the Servicer pinned it so it would stay
  // across messages (story pinned-pane), and dismissing it on a timer takes
  // back the one thing pinning was for.
  //
  // Office.js exposes no way to ask whether a task pane is pinned — the only
  // signal is that pinned panes receive `ItemChanged`, which says nothing
  // until the Servicer switches message. Treating "not yet observed" as
  // unpinned would close a pinned pane on the first run, so there is no safe
  // inference to make and the pane is simply left alone. Returning to the
  // queue is the better landing anyway: the pane is queue-first (story
  // pinned-pane, AC-2), so what is behind the drawer is the work.
  //
  // `App.tsx`'s own 10s auto-close on `completed` still hides the pane and has
  // the same defect; it predates this view and is untouched here.
  const [stepViewAutoCloseHeld, setStepViewAutoCloseHeld] = useState(false);
  // WHAT THE SERVICER IS DOING HOLDS IT, not only the Hold button.
  //
  // The body renders the carrier-evidence uploader UNDER this view: the card is
  // gated on the evidence DECISION, not on the parked status, so a run carrying
  // it before it reaches the gate is `running` and shows a dropzone beneath a
  // 10-second countdown. Dragging two files out of a folder takes longer than
  // that, the pick is local state in the card, and the dismissal dropped both
  // with nothing to recover (operator, 2026-09-14).
  //
  // Same flag as the button, so it is one hold with two ways in: permanent for
  // this instance, cleared only by a different run, and — because a countdown
  // never answered anything — incapable of deciding anything by being held.
  const holdStepViewAutoClose = useCallback(() => {
    setStepViewAutoCloseHeld(true);
  }, []);

  // IT COUNTS ONCE. A DEADLINE, NOT A DURATION.
  //
  // The budget used to be re-armed by a boolean that included `!push.loading`,
  // and `loading` flips on EVERY poll and push refresh. So a run counted 10, 9,
  // 8, refreshed, counted 10 again, and on a busy run never reached zero — two
  // or three restarts before the first phase change. A duration cannot survive
  // that, because every re-arm is indistinguishable from a first arm.
  //
  // A deadline can: it is set ONCE per run, the wait is whatever remains of it
  // on any given render, and a refresh that re-runs this component changes
  // nothing about when it expires. It is cleared only by the three things that
  // genuinely end this countdown — a different run, a Hold, or the run leaving
  // the running view.
  const [stepViewAutoCloseAt, setStepViewAutoCloseAt] = useState<number | null>(
    null,
  );
  useEffect(() => {
    // A different run gets a fresh countdown; a hold is per-instance (D5).
    setStepViewAutoCloseHeld(false);
    setStepViewAutoCloseAt(null);
  }, [instanceId]);
  // NOT ARMED UNTIL THERE IS SOMETHING TO READ. `push.status` is null while the
  // first read is in flight, and a null status is the "not loaded yet" sentinel
  // that `pickDrawerStatusFamily` deliberately maps to the `active` family — so
  // `stepViewName` reads "running" for a drawer that is still showing its
  // loading placeholder. Arming on that started the clock on an empty pane: a
  // read slower than the budget spent it entirely, and the view was dismissed
  // before it had rendered anything at all.
  //
  // So the countdown waits for a status that actually arrived. `push.loading`
  // is checked as well, so a refetch that clears the status mid-flight cannot
  // leave a timer running against a placeholder either.
  //
  // `push.status` is the RIGHT signal and `push.loading` is the wrong one:
  // `useInstancePush` clears the status only when the INSTANCE changes, so a
  // status that has arrived stays arrived across every refetch, while
  // `loading` flaps throughout. Reading the flag is what restarted the
  // countdown on each refresh.
  const stepViewDataReady = push.status != null;
  const stepViewShowsRunning =
    open && effectiveMode === "live" && stepViewName === "running";

  useEffect(() => {
    if (!stepViewShowsRunning || stepViewAutoCloseHeld) {
      setStepViewAutoCloseAt(null);
      return;
    }
    if (!stepViewDataReady) return;
    // `?? Date.now() + …` is what makes this arm once: after the first pass the
    // deadline is already set, and every later render keeps it.
    setStepViewAutoCloseAt(
      (prior) => prior ?? Date.now() + STEP_VIEW_AUTO_CLOSE_SECONDS * 1000,
    );
  }, [stepViewShowsRunning, stepViewDataReady, stepViewAutoCloseHeld]);
  // Read through a ref so that a caller passing a fresh closure every render
  // cannot restart the countdown on each one, which would make it never fire.
  const stepViewCloseRef = useRef(onAutoClose ?? onClose);
  stepViewCloseRef.current = onAutoClose ?? onClose;
  useEffect(() => {
    if (stepViewAutoCloseAt == null || stepViewAutoCloseHeld) return;
    // Both deps are stable once armed, so this schedules exactly one timeout
    // for the life of the countdown. `Math.max` covers a deadline already past
    // — a pane asleep through the whole budget fires on the next frame rather
    // than never.
    const timer = window.setTimeout(
      () => stepViewCloseRef.current(),
      Math.max(0, stepViewAutoCloseAt - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [stepViewAutoCloseAt, stepViewAutoCloseHeld]);

  // What the Servicer reads, from the same deadline the timer above uses, so
  // the digits and the action cannot drift. Memoised on the DEADLINE rather
  // than recomputed per render: `AnnouncedCountdown` restarts itself whenever
  // `seconds` changes, so a value that ticked down here would reset the very
  // thing it describes. On a genuine remount this recomputes, and the countdown
  // RESUMES at whatever is left instead of starting over.
  const stepViewAutoCloseSeconds = useMemo(
    () =>
      stepViewAutoCloseAt == null
        ? null
        : Math.max(0, Math.ceil((stepViewAutoCloseAt - Date.now()) / 1000)),
    [stepViewAutoCloseAt],
  );

  // 26747: the pane opening ON A DECISION is a touchpoint; leaving it (by
  // answering, by closing, or because the run moved on) closes the pair. The
  // auto-close firing is deliberately NOT one, and neither is Hold — that is a
  // cancelled leave, not an interaction with the decision.
  //
  // `emitTouchpoint` is a shim that records nothing yet; WT-P2's provider
  // replaces its body and leaves this call site alone.
  const decisionTouchpointKey =
    stepViewsEnabled &&
    open &&
    (stepViewName === "brief" || stepViewName === "exception")
      ? `${instanceId ?? ""}:${stepViewName}`
      : null;
  useEffect(() => {
    if (!decisionTouchpointKey) return;
    const [id, view] = decisionTouchpointKey.split(":");
    emitTouchpoint("hitl_answer", "arrive", { instance_id: id, view });
    return () => {
      emitTouchpoint("hitl_answer", "leave", { instance_id: id, view });
    };
  }, [decisionTouchpointKey]);

  // ── pre_launch state ──────────────────────────────────────────────
  const [values, setValues] = useState<Record<string, unknown>>(
    prefill ?? {},
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [autoLaunching, setAutoLaunching] = useState(false);
  const autoLaunchedRef = useRef(false);

  // Concurrent-run confirmation. Set when the substrate refused this launch
  // because another non-terminal run already exists for the same email AND we
  // managed to identify that run. Holds the blocking instance (so the dialog
  // can say WHICH run and what state it is in) alongside the form values, so
  // "Continue" can replay the identical launch with an acknowledgement.
  const [concurrentPrompt, setConcurrentPrompt] = useState<{
    instance: ActiveWorkflow;
    values: Record<string, unknown>;
  } | null>(null);

  // ── per-run oversight level (pre_launch only) ─────────────────────
  // Seeded to DEFAULT_OVERSIGHT_LEVEL ("LOW"), then preselected from the
  // operator's stored preference (substrate get_oversight_level via the
  // product proxy). The form always submits an explicit level and the
  // substrate persists the submitted level as the new standing preference —
  // so without this preselect, an untouched HIGH default silently overwrote
  // the stored preference on every launch.
  const [oversightLevel, setOversightLevel] = useState<OversightLevel>(DEFAULT_OVERSIGHT_LEVEL);
  // A fetch that resolves AFTER the user touches the dial must not clobber
  // their selection.
  const oversightTouchedRef = useRef(false);
  const handleOversightLevelChange = useCallback((v: OversightLevel) => {
    oversightTouchedRef.current = true;
    setOversightLevel(v);
  }, []);
  // Keyed on the entry's id (not the object): a catalog refetch that
  // re-creates the entry must not re-fire the fetch. Dev still calls twice
  // per open — StrictMode's deliberate double-mount — which the cancelled
  // flag and touched-ref make harmless; prod calls once.
  const catalogEntryId = catalogEntry?.id ?? null;
  const showOversight = catalogEntry ? shouldShowOversight(catalogEntry) : false;
  useEffect(() => {
    if (!catalogEntryId || !showOversight) return;
    let cancelled = false;
    void orchestratorApi.getMyOversightLevel().then((level) => {
      if (!cancelled && level && !oversightTouchedRef.current) {
        setOversightLevel(level);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [catalogEntryId, showOversight]);

  // ── Dedicated-Service-Principal mode (SP-mode workflows: shouldShowSpToggle) ──────
  // Default ON (operator decision D5): runs sign in to Epic as the shared pool
  // (v2) or the caller's dedicated SP (v1.2). `use_sp_login` rides `inputs` on
  // launch (gated by shouldShowSpToggle). `spCredsReady` (null = not yet
  // loaded) gates the launch: SP mode with no usable SP would stop the run
  // immediately, so the button is blocked and handleLaunch backstops it. The
  // AM only switches to their own login from the fallback block PreLaunchBody
  // shows when the SP is unavailable (story 28633 — there is no options
  // disclosure any more).
  const [useSpLogin, setUseSpLogin] = useState(true);
  const [spCredsReady, setSpCredsReady] = useState<boolean | null>(null);

  // The readiness check runs at DRAWER level, on open.
  // Bumped to force a re-probe (Retry, or Undo back to the default sign-in).
  // Without it this effect keyed on [catalogEntry] alone
  // and therefore ran exactly ONCE per drawer session: a single transient
  // failure — a slow SP-pool listing, an auth blip — latched "not ready" for the
  // whole session and the operator was stuck at a Submit they could not press,
  // reading "No service principals in pool" against a healthy two-slot pool
  // (reported 2026-08-19). Readiness is remote, mutable state; probing it once
  // and trusting the answer forever is the bug.
  const [spReadinessNonce, setSpReadinessNonce] = useState(0);
  const [spReadinessChecking, setSpReadinessChecking] = useState(false);
  const recheckSpReadiness = useCallback(() => setSpReadinessNonce((n) => n + 1), []);

  useEffect(() => {
    if (!catalogEntry || !shouldShowSpToggle(catalogEntry)) return;
    let cancelled = false;
    setSpReadinessChecking(true);
    // Two generations, two DIFFERENT readiness questions — see usesSpPool.
    // v1.2 asks "are MY dedicated-SP creds saved?"; v2 asks "does the SHARED
    // pool hold at least one slot with credentials?". Asking v1.2's question for
    // a v2 run would pass for an operator with personal SP creds while the
    // pool is empty, and the run would still dead-end on sp_credentials_missing
    // — which is the exact failure this gate exists to prevent.
    // An errored pool check is "not ready" too: the AM sees the same fallback
    // (it is not theirs to fix either way) and can Retry or use their own login.
    const pooled = usesSpPool(catalogEntry);
    const check: Promise<boolean> = pooled
      ? fetchSpPoolReadiness().then((r) => spPoolPermitsLaunch(r))
      : fetchSpCredsReady();
    void check
      .then((ready) => {
        if (cancelled) return;
        setSpCredsReady(ready);
      })
      .finally(() => {
        if (!cancelled) setSpReadinessChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [catalogEntry, spReadinessNonce]);

  // Clear transient error/submit state when the user navigates to a different instance.
  useEffect(() => {
    setSubmitError(null);
    setSubmitting(false);
  }, [instanceId]);

  // Merge async prefill values when the background prefill call resolves.
  // prefillLoading transitions true→false exactly once per drawer session;
  // we use that edge to merge without clobbering subsequent user edits.
  const prevPrefillLoadingRef = useRef(prefillLoading);
  useEffect(() => {
    const wasLoading = prevPrefillLoadingRef.current;
    prevPrefillLoadingRef.current = prefillLoading;
    if (wasLoading && !prefillLoading && prefill) {
      setValues((prev) => ({ ...prev, ...prefill }));
    }
  }, [prefillLoading, prefill]);

  // ── live state: cancel confirmation ───────────────────────────────
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // ── terminal state: artifacts (only fetch when not provided) ─────
  const [ownArtifacts, setOwnArtifacts] = useState<ArtifactDto[]>([]);
  const [ownArtifactsLoading, setOwnArtifactsLoading] = useState(false);
  const artifactsFetchedFor = useRef<string | null>(null);

  // Tracks whether the Artifacts accordion is currently open. Reported up
  // from the DrawerSection via ``onExpandedChange`` (fires on mount with the
  // localStorage-resolved initial state, then on every user toggle). Used
  // to gate ``shouldFetchArtifacts`` below so paging through instances with
  // the accordion closed doesn't trigger a network call (and the resulting
  // image byte transfers). Defaults false — fail-closed; the DrawerSection's
  // mount effect immediately updates this if the user has it persistently
  // open. The drawer-section family layer (terminal vs live) reuses the
  // same persistKey, so the resolved value carries across instance pages.

  // ── HITL Decision History (audit trail) ─────────────────────────────
  // Self-fetch parallels the artifacts pattern above. Visible during
  // paused_hitl + terminal so the AM can retrace prior decisions on a
  // multi-HITL workflow without leaving the drawer.
  const [hitlEvents, setHitlEvents] = useState<HitlEvent[]>([]);
  const [hitlLoading, setHitlLoading] = useState(false);
  const hitlFetchedFor = useRef<string | null>(null);

  // Retry counter — increments on every retry click so the suffix is
  // unique even if the user retries the same failure twice.
  const retryAttemptRef = useRef(0);
  const [retrying, setRetrying] = useState(false);

  // ── Phase 2: terminal banner for cross-client sync ─────────────────
  // When the workflow finishes due to an action in ANOTHER client (not
  // a local cancel/submit/retry), surface a compact banner so the user
  // knows the drawer's content is no longer live. The body underneath
  // continues to render the terminal layout (frozen pipeline +
  // artifacts + result envelope) so the final state is visible at a
  // glance even before the user dismisses the banner.
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const externalTerminalRef = useRef(false);
  const prevIsTerminalRef = useRef(false);
  // True once the hook has returned its FIRST authoritative status for the
  // current instance. Until then `prevIsTerminalRef` is unseeded and the
  // transition effect MUST NOT fire — otherwise a stale row from the
  // active-instances list can claim the workflow is "running" while the
  // hook's first fetch reveals it's actually been "completed" for a while,
  // and the false live→terminal transition spuriously raises the "finished
  // elsewhere" banner.
  const hookSeededRef = useRef(false);
  // Local-action-in-flight ref kept always-fresh from cancelling /
  // retrying so the effect watching `ownPush.isTerminal` reads a live
  // value (the effect's closure would otherwise see stale values).
  const localActionInFlightRef = useRef(false);
  // hitlSubmittedRef stays true for the session once the user submits a HITL
  // response — the workflow may take seconds/minutes to complete after that,
  // and by then cancelling/retrying are both false, so we track it separately.
  const hitlSubmittedRef = useRef(false);
  localActionInFlightRef.current = cancelling || retrying || hitlSubmittedRef.current;
  // Reset banner state when the user navigates to a different instance.
  useEffect(() => {
    setBannerDismissed(false);
    externalTerminalRef.current = false;
    hitlSubmittedRef.current = false;
    hookSeededRef.current = false;
    prevIsTerminalRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);
  // ── SignalR ping (dev diagnostic) ─────────────────────────────────
  interface PingMessage { message: string; sender: string; timestamp: string; }
  const [pingMessages, setPingMessages] = useState<PingMessage[]>([]);
  const [pingSending, setPingSending] = useState(false);

  useEffect(() => { setPingMessages([]); }, [instanceId]);

  useEffect(() => {
    if (!instanceId || !ownPush.isLiveConnected) return;
    return ownPush.subscribe<PingMessage>("wf_test_ping", (msg) => {
      setPingMessages((prev) => [msg, ...prev].slice(0, 5));
    });
  }, [instanceId, ownPush.isLiveConnected, ownPush.subscribe]); // eslint-disable-line react-hooks/exhaustive-deps

  // running-drawer: the more-information pane's open state, owned HERE (this
  // component is stable for the life of the current instance — it isn't torn
  // down by a status/family change) so it survives step/phase updates instead
  // of resetting to closed every time new data arrives.
  const [moreInfoOpen, setMoreInfoOpen] = useState(false);
  useEffect(() => { setMoreInfoOpen(false); }, [instanceId]);
  const onMoreInfoClose = useCallback(() => setMoreInfoOpen(false), []);

  const sendPing = useCallback(async () => {
    if (!instanceId || pingSending) return;
    setPingSending(true);
    try {
      await orchestratorApi.pingSignalR(instanceId, "plugin");
    } finally {
      setPingSending(false);
    }
  }, [instanceId, pingSending]);

  // Watch for the live → terminal transition. Mark as external when no
  // local action was in flight at the moment the terminal status
  // arrived. Drawers opened directly into terminal mode (e.g. from the
  // active list for an already-completed workflow) skip this — they
  // never had a "live" reference state to transition from.
  //
  // The hook's first authoritative status seeds the baseline. Until it
  // lands, `ownPush.isTerminal` is derived from a possibly-stale row in
  // the active-instances list, and using it as the "previous" baseline
  // would misfire the banner the moment the hook's first fetch reveals
  // the workflow was already terminal at drawer-open.
  useEffect(() => {
    if (ownPush.status == null) return;
    const isTerminalNow = ownPush.isTerminal;
    if (!hookSeededRef.current) {
      hookSeededRef.current = true;
      prevIsTerminalRef.current = isTerminalNow;
      return;
    }
    const wasTerminal = prevIsTerminalRef.current;
    prevIsTerminalRef.current = isTerminalNow;
    if (
      mode === "live" &&
      !wasTerminal &&
      isTerminalNow &&
      !localActionInFlightRef.current
    ) {
      externalTerminalRef.current = true;
    }
  }, [ownPush.isTerminal, ownPush.status, mode]);
  // Banner shows when push observed an external terminal transition AND
  // the user hasn't dismissed it yet AND we have a known terminal status
  // to choose the verb from.
  const terminalStatusKey = (push.status?.status ?? "").toLowerCase();
  const showFinishedElsewhereBanner =
    externalTerminalRef.current &&
    !bannerDismissed &&
    (terminalStatusKey === "completed" ||
      terminalStatusKey === "cancelled" ||
      terminalStatusKey === "timeout_cancelled" ||
      terminalStatusKey === "failed");

  // Fetch artifacts whenever the more-information pane is open. Deliberately
  // NOT gated on the run's status — that gate is ora's shape
  // (`ArtifactsSection`: expanded + a `${instanceId}:${updated_at}` cache key,
  // no status test) and the reason is that a status allowlist here has to
  // restate which statuses are "live enough" to have artifacts, and it drifted
  // twice. It listed terminal + {paused_hitl, warm_hitl, running}, which
  // silently excluded BOTH parked statuses — `pending_email_send` and
  // `pending_edocs` are not in TERMINAL_STATUSES either, so for a run waiting
  // on the AM to send its reply the fetch was unreachable and the substrate
  // never saw a single artifact request (evidence: instance
  // 3bece478-0885-41fd-af15-7410d3dce80b had 4 screenshots + an .eml on disk
  // and zero artifact calls in the substrate log, while ora's drawer listed
  // them fine).
  //
  // The lazy gate stays, but it moved: there is no Artifacts accordion any
  // more (artifacts are part of the run record, behind the one
  // more-information entry point), so the pane's open state IS the signal the
  // accordion used to give. Paging through instances with the pane closed
  // still fires no artifacts API call and loads no image bytes, and pre_launch
  // — which has no entry point — is still subsumed.
  //
  // This gate is load-bearing in the other direction too. When the accordion
  // went away, a gate left on `artifactsAccordionOpen` could never become
  // true, and the add-in would have silently stopped requesting artifacts
  // altogether — the exact failure the paragraph above was written about.
  const shouldFetchArtifacts =
    instanceId !== null &&
    instanceId !== undefined &&
    artifactsProp === undefined &&
    moreInfoOpen;
  // `updated_at` moves whenever the substrate records anything — a phase
  // boundary, a status transition, a new artifact — so one key covers what the
  // old terminal/hitl/running branches were each reaching for, and re-fetches
  // exactly when there may be something new rather than on a timer.
  const artifactFetchKey = instanceId
    ? `${instanceId}:${ownPush.status?.updated_at ?? ""}`
    : null;
  useEffect(() => {
    if (!shouldFetchArtifacts || !artifactFetchKey || !instanceId) return;
    if (artifactsFetchedFor.current === artifactFetchKey) return;
    artifactsFetchedFor.current = artifactFetchKey;
    setOwnArtifactsLoading(true);
    // No abort/"cancelled" guard on purpose: the ref above already dedupes,
    // and under StrictMode's mount→unmount→mount the second pass is blocked by
    // that ref — so cancelling the first pass's promise would throw away the
    // ONLY fetch and nothing would ever render. ora's loader is written the
    // same way for the same reason.
    void orchestratorApi
      .getArtifacts(instanceId)
      .then((r) => {
        setOwnArtifacts(r.artifacts ?? []);
      })
      .catch((err) => {
        logger.warn("InstanceDetailDrawer: getArtifacts failed", {
          instance_id: instanceId,
          error: String(err),
        });
        setOwnArtifacts([]);
      })
      .finally(() => setOwnArtifactsLoading(false));
  }, [shouldFetchArtifacts, artifactFetchKey, instanceId]);

  const artifacts = artifactsProp ?? ownArtifacts;
  const artifactsLoading = artifactsLoadingProp ?? ownArtifactsLoading;

  const fetchArtifactPayload = useCallback(
    async (iid: string, filename: string) => {
      const result = await orchestratorApi.getArtifactPayload(iid, filename);
      return result.payload;
    },
    [],
  );

  // Single hoisted lightbox for the whole drawer — ``useArtifactLightbox``
  // keeps click wiring identical to ora's shared ``WorkflowArtifactsGallery``.
  const { onArtifactClick: handleArtifactClick, artifactLightbox } =
    useArtifactLightbox({
      artifacts,
      instanceId,
      fetchArtifactPayload,
    });

  // Same treatment as artifacts, and the same reason: the old status
  // allowlist here was even narrower (terminal + {paused_hitl, warm_hitl},
  // so not even `running`), which meant a run parked at pending_email_send /
  // pending_edocs never loaded its decision history either.
  const shouldFetchHitlHistory =
    instanceId !== null &&
    instanceId !== undefined &&
    effectiveMode !== "pre_launch";
  // ora's key shape (`HitlHistorySection`): terminal freezes to a single
  // fetch — no new decisions can arrive — while a live run keys off status
  // AND `updated_at`, so a second decision within the same status still
  // re-fetches. The frozen key also replaces the separate "re-fetch at
  // terminal entry" effect this used to need: crossing into terminal changes
  // the key, which IS the re-fetch.
  const hitlFetchKey = !instanceId
    ? null
    : effectiveMode === "terminal"
      ? `${instanceId}:frozen`
      : `${instanceId}:live:${ownPush.status?.status ?? ""}:${ownPush.status?.updated_at ?? ""}`;
  useEffect(() => {
    if (!shouldFetchHitlHistory || !hitlFetchKey || !instanceId) return;
    if (hitlFetchedFor.current === hitlFetchKey) return;
    hitlFetchedFor.current = hitlFetchKey;
    setHitlLoading(true);
    void orchestratorApi
      .getHitlHistory(instanceId)
      .then((r) => {
        setHitlEvents(r.events ?? []);
      })
      .catch((err) => {
        logger.warn("InstanceDetailDrawer: getHitlHistory failed", {
          instance_id: instanceId,
          error: String(err),
        });
        setHitlEvents([]);
      })
      .finally(() => setHitlLoading(false));
  }, [shouldFetchHitlHistory, hitlFetchKey, instanceId]);

  const handleLaunch = useCallback(
    async (
      next: Record<string, unknown>,
      // Set ONLY by the concurrent-run confirm dialog's "Continue": the
      // instance_id of the in-flight run the operator was shown and chose to
      // launch alongside. Rides metadata to the substrate's (6b) guard.
      acknowledgedInstanceId?: string,
    ) => {
      if (!catalogEntry) {
        setSubmitError("No workflow selected");
        return;
      }
      // 26747: the approve click. `correction` rides beside it — and ONLY
      // beside it — when the Servicer changed something ora had proposed: a
      // correction is an EXCEPTION touchpoint, and counting an untouched
      // approval as one would make every clean run look like rework. Compared
      // against the prefill ora produced, not against an empty form, so
      // filling in a field ora left blank does not read as a correction
      // either.
      const correctedFields = Object.keys(prefill ?? {}).filter(
        (key) =>
          JSON.stringify((prefill ?? {})[key]) !== JSON.stringify(next[key]),
      );
      emitTouchpoint("brief_launch", "arrive", {
        workflow_id: catalogEntry.id,
      });
      emitTouchpoint("brief_launch", "leave", { workflow_id: catalogEntry.id });
      if (correctedFields.length > 0) {
        emitTouchpoint("correction", "arrive", {
          workflow_id: catalogEntry.id,
          corrections_count: correctedFields.length,
        });
        emitTouchpoint("correction", "leave", {
          workflow_id: catalogEntry.id,
          corrections_count: correctedFields.length,
        });
      }
      // Workflows that reply to the triggering email need ``graph_message_id``
      // (silent capture from ``Office.context.mailbox.item.itemId``, surfaced
      // here as the ``graphMessageId`` prop). They cannot run from non-mail
      // surfaces (calendar / contacts panes) or before the mailbox item has
      // been read. Gate on the SAME value we inject below — that way a launch
      // can never slip through with the id missing and silently skip the reply
      // draft (regression: instance 44a7dc39). carrier_mortgagee_update can
      // ONLY be launched from an Outlook mail item, so its id is mandatory.
      // Set moved to `launchRules.NEEDS_MAIL_ITEM_WORKFLOWS` (core) — the
      // check itself, and its position ahead of the other guards, stays here.
      if (requiresGraphMessageId(catalogEntry) && !graphMessageId) {
        setSubmitError(
          "Open this from a mail item — the workflow needs the email's "
          + "id to create a reply draft.",
        );
        return;
      }
      // SP mode (mortgagee_update_v1_2) needs the dedicated SP's Epic creds set,
      // else the run stops immediately (sp_credentials_missing). HARD gate (R4):
      // in SP mode only a CONFIRMED-ready status launches — `null` (check not
      // finished / failed) blocks too, since launching on an unknown status
      // would still dead-end the run.
      if (shouldShowSpToggle(catalogEntry) && useSpLogin && spCredsReady !== true) {
        // A backstop only — the button is already blocked, and the fallback
        // block above it offers the remedy (own login, or Retry). Same words
        // as that block, so the two can never disagree.
        const pooled = usesSpPool(catalogEntry);
        setSubmitError(
          spCredsReady === null
            ? "Still checking Epic sign-in — try again in a moment."
            : pooled
              ? "The shared Epic sign-in isn't available right now. Use your own "
                + "Epic login, or Retry."
              : "Your Epic Service Principal sign-in isn't set up. Use your own "
                + "Epic login, or Retry.",
        );
        return;
      }
      // Client-selection gate (prefill-data-threading-and-gate, AC#3). A
      // mortgagee-update threads the selected client's chosen policy/carrier
      // into ``already_extracted`` for prefill — with no client selected there
      // is nothing to thread, so block the launch entirely. This is the
      // authoritative backstop: even if the disabled launch affordance in
      // PreLaunchBody is re-enabled (devtools), launch is never triggered
      // without a client. Scoped to the mortgagee-update ids ONLY (R3).
      if (requiresClient(catalogEntry) && clientSkProp == null) {
        setSubmitError("Select a client to continue.");
        return;
      }
      // Policy-membership gate (typo'd/another client's number) + the
      // effective-date-within-term backstop + the chosen-term resolution —
      // Story 26752 / plan glittery-stargazing-treehouse Phase 3: moved to
      // the shared `resolveLaunchTerm` core module. Ora's triage route calls
      // the SAME function, so a second inline copy can no longer drift.
      //
      // WHERE the per-action values live in this launch: the v2 contract
      // moved nine of them inside `actions[0]`; every other workflow in the
      // catalog — including `mortgagee_update_v1_2`, the one AMs launch
      // today — is still flat, and this one handler serves all of them.
      // `resolveLaunchTerm` decides from the SCHEMA (via `binding`), so an
      // un-reshaped workflow takes today's exact path, reading the merged
      // `flat` view it returns rather than raw `next` — reading `next`
      // directly after a reshape would fail every guard OPEN, not loudly
      // (the future-term regression, instances 3b35db7f / e036c04d).
      // `binding` is built once and handed to both this and
      // `assembleLaunchInputs` below (their own shared contract).
      const binding = buildLaunchBinding(catalogEntry.input_schema ?? null);
      const termResult = resolveLaunchTerm(binding, next);
      if (termResult.error) {
        setSubmitError(termResult.error);
        return;
      }
      const { flat, chosenTerm } = termResult;
      const idempotency_key = genIdempotencyKey();
      // Per-email identity — built by the shared ONE producer seam
      // (common/workflows/launch/launchPayload.ts), never inline here. It
      // yields the DECLARED `internet_message_id` input plus the unchanged
      // `graph_message_id` Graph handle.
      //
      // Strict only when the target workflow DECLARES the identity required
      // (carrier_mortgagee_update_v2): the substrate refuses such a launch, so
      // refusing here with the same instruction beats sending a payload we
      // already know is rejected. The live fleet (mortgagee_update_v1_2)
      // declares no identity and must keep launching from Outlook hosts that
      // hand us an empty item.internetMessageId (see
      // orchestratorApi.buildAnalyzeEmailPayload).
      let identityInputs: Record<string, string>;
      try {
        identityInputs = buildLaunchIdentityInputs(
          { internetMessageId, graphMessageId },
          {
            requireIdentity: launchRequiresMessageIdentity(
              catalogEntry.input_schema ?? null,
            ),
          },
        );
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : String(err));
        return;
      }
      // Mirror ora's launch payload: include ``client_name`` and ``client_sk``
      // in the workflow's ``inputs`` (not just metadata). Orchestration bodies
      // read from ``start_input["inputs"]`` to derive their client context —
      // the carrier_mortgagee_update body specifically does
      // ``inputs.get("client_name")`` and falls back to no client when the
      // field is missing, then overwrites the row's metadata via emit_push.
      // Without these keys in inputs the client name is lost between launch
      // and the completed Run + Client sections.
      // The run-level values only the LAUNCHER knows. Shared by both branches
      // and by the retry, so a key can never ride one path and not the other.
      //
      // ⚠️ The term pair is deliberately ABSENT here. Today's flat payload
      // injects `policy_effective_date` / `policy_expiration_date` at run level
      // from `chosenTerm`; under the reshaped contract those keys are declared
      // PER ACTION, so a run-level injection is filtered out by
      // `declaredKeys.runLevel` and the action would go out carrying a bare
      // policy number — the exact 2026-08-05 wrong-term defect. On the reshaped
      // contract the term control has already written the full declared
      // term-identity subset INTO the action slice; the flat branch below keeps
      // both injections byte-for-byte.
      const launcherInputs: Record<string, unknown> = {
        // Mirror ora's launch payload: `client_name` and `client_sk` ride the
        // workflow's `inputs` (not just metadata). Orchestration bodies read
        // `start_input["inputs"]` to derive their client context, and without
        // these the client name is lost between launch and the completed run.
        ...(clientSkProp != null ? { client_sk: clientSkProp } : {}),
        ...(clientNameProp ? { client_name: clientNameProp } : {}),
        // Silent-capture identity fields (not form fields — only the launcher
        // knows them), from the ONE producer seam above.
        ...identityInputs,
        idempotency_key,
        // Per-run oversight level: rides `inputs` (not metadata) so
        // orchestration reads it from start_input["inputs"] →
        // initial_state["oversight_level"] → execution-plane cache. ONLY for
        // v2 workflows that declare oversight — legacy v1 has no oversight
        // concept and must never receive it.
        ...(shouldShowOversight(catalogEntry)
          ? { oversight_level: oversightLevel }
          : {}),
        // Dedicated-SP toggle — SP-mode workflows only (shouldShowSpToggle).
        ...(shouldShowSpToggle(catalogEntry)
          ? { use_sp_login: useSpLogin }
          : {}),
      };

      // Shared assembler (plan Phase 3) — filters both slices against the
      // schema's own declared keys on the reshaped contract (AC-6c) and keeps
      // today's exact spread, `...injected` LAST, on the flat contract. Same
      // module `handleRetry` below calls, so a retry cannot emit a different
      // shape from the launch it retries.
      const assembled = assembleLaunchInputs({
        binding,
        next,
        injected: launcherInputs,
        chosenTerm,
      });
      if (assembled.dropped.length > 0) {
        logger.warn("InstanceDetailDrawer: launch dropped undeclared keys", {
          workflow_id: catalogEntry.id,
          dropped: assembled.dropped,
        });
      }
      const inputs = assembled.inputs;
      // Derive display name for the initial trigger metadata: prefer the
      // resolved prop (canonical DB name), fall back to whatever the form
      // had in ``next`` (extraction hint seeded into values by App.tsx).
      const derivedClientName =
        clientNameProp ?? (typeof next.client_name === "string" ? next.client_name : null);
      const metadata = buildLaunchMetadata({
        client: { sk: clientSkProp, name: derivedClientName },
        // From the MERGED record (`termResult.flat`), not run-level `next`:
        // after normalisation the policy number lives on the action, and
        // reading `next` here would leave `metadata.client_context.policy`
        // off every reshaped launch.
        policyNumber:
          typeof flat.policy_number === "string" ? flat.policy_number : null,
        chosenTerm,
        // Feedback-loop anchor: the analyzed email's RFC 5322 Message-ID. As
        // of the declared-identity change this is a MIRROR of the
        // `internet_message_id` INPUT above, not its only home — the
        // declared input is authoritative. It stays for one rollout window
        // because process_launch_feedback and the pane's already-running
        // lookup still resolve instances started before the declared field
        // existed; removing it would strand those runs' panes.
        internetMessageId,
        // Operator saw the in-flight run named here and chose to launch
        // anyway. Scoped to that exact instance_id rather than a generic
        // force flag: the substrate's (6b) guard only yields when the ack
        // matches the run IT finds, so a confirmation that went stale (a
        // different run started while the dialog was open) still blocks
        // instead of waving through a run the operator never saw. Persisted
        // on the new instance row as the audit trail for the override.
        extras: acknowledgedInstanceId
          ? {
              concurrent_run_override: {
                acknowledged_instance_id: acknowledgedInstanceId,
              },
            }
          : undefined,
      });
      setSubmitting(true);
      setSubmitError(null);
      try {
        const result = await orchestratorApi.triggerWorkflow(
          catalogEntry.id,
          inputs,
          metadata,
        );
        onLaunched?.({
          instance_id: result.instance_id,
          workflow_id: result.workflow_id,
          idempotency_key,
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Launch failed";
        // Concurrent-run conflict (substrate start_workflow step 6b): another
        // non-terminal run already exists for this email. That is a WARNING,
        // not a dead end — an email can legitimately need a second run (a wrong
        // draft, a second policy named in the same email, a failed tail), and
        // only the operator knows which case this is. So resolve WHICH run is
        // blocking, then let them decide.
        //
        // Only on a first attempt: if this launch already carried an
        // acknowledgement and STILL conflicted, the ack no longer matches what
        // the substrate found (a different run started in between), so surface
        // the error instead of re-prompting in a loop.
        if (
          err instanceof ApiError
          && err.status === 409
          && !acknowledgedInstanceId
        ) {
          const blocking = await findBlockingInstanceForEmail({
            emailId: graphMessageId,
            internetMessageId,
          });
          if (blocking) {
            logger.info("InstanceDetailDrawer: launch blocked by in-flight run", {
              workflow_id: catalogEntry.id,
              blocking_instance_id: blocking.instance_id,
              blocking_status: blocking.status,
            });
            // Observation hook (story pinned-pane). The guard's own reason
            // code when the bridge forwarded one, else the HTTP status —
            // never the message, which carries the owner's display name.
            onLaunchRefused?.({
              instance_id: blocking.instance_id,
              reason_code: refusalReasonCode(err),
            });
            setConcurrentPrompt({ instance: blocking, values: next });
            return; // `finally` still clears `submitting`
          }
          // Couldn't identify the blocking run (anchors missing, list call
          // failed). An un-actionable dialog would be worse than the message,
          // which now carries the blocking status — fall through and show it.
        }
        setSubmitError(message);
        logger.warn("InstanceDetailDrawer: launch failed", {
          workflow_id: catalogEntry.id,
          error: message,
        });
      } finally {
        setSubmitting(false);
      }
    },
    [catalogEntry, onLaunched, onLaunchRefused, clientSkProp, clientNameProp, oversightLevel, useSpLogin, spCredsReady, graphMessageId, internetMessageId, prefill],
  );

  // ── Auto-launch: fire immediately when all required schema fields are
  //    present in prefill (idempotency_key is always generated, skip it).
  //    Placed after handleLaunch to avoid TDZ reference in the deps array.
  useEffect(() => {
    if (!autoLaunch || !catalogEntry || autoLaunchedRef.current) return;
    if (prefillLoading) return;
    // The required-field gate reads the same shape prefill now emits. After the
    // reshape `required == ["actions", …]` and prefill emits `actions`, so a
    // flat presence test would pass on the KEY while proving nothing about the
    // values — auto-launch would start real runs on empty payloads. The shared
    // predicate descends into `actions[0]` and checks the keys the ITEM schema
    // declares required, and skips `x-hidden` launcher-injected inputs (which
    // prefill never emits and the AM never sees).
    const schema = (catalogEntry.input_schema ?? null) as Record<
      string,
      unknown
    > | null;
    if (!prefillSatisfiesRequired(schema, prefill ?? null)) return;
    autoLaunchedRef.current = true;
    setAutoLaunching(true);
    void handleLaunch(prefill ?? {});
  }, [autoLaunch, catalogEntry, prefill, handleLaunch, prefillLoading]);

  const handleConfirmCancel = useCallback(async () => {
    if (!instanceId) return;
    setCancelling(true);
    try {
      await orchestratorApi.cancelInstance(instanceId);
      onCancelled?.(instanceId);
    } catch (err) {
      logger.warn("InstanceDetailDrawer: cancel failed", {
        instance_id: instanceId,
        error: String(err),
      });
    } finally {
      setCancelling(false);
      setConfirmingCancel(false);
    }
  }, [instanceId, onCancelled]);

  const handleRetry = useCallback(async () => {
    if (!catalogEntry || !runLaunchableInPlugin) return;
    retryAttemptRef.current += 1;
    const baseKey = previousIdempotencyKey ?? genIdempotencyKey();
    const newKey = `${baseKey}-retry-${retryAttemptRef.current}`;
    setRetrying(true);
    try {
      // Reuse the same input values that were captured on the original
      // run when available (held in `values` state). Otherwise just send
      // the idempotency_key — substrate will reject if required fields
      // are missing, which we surface in submitError. Also re-include
      // client_name + client_sk so the orchestration's metadata derivation
      // sees them (mirrors ``handleLaunch`` above).
      // Re-derive the picked term for the retry. `values` carries `policy_options`
      // and the term key (both ride the form state), but NOT the resolved
      // policy_effective_date / policy_expiration_date — those are injected into
      // `inputs` by handleLaunch, not written back to form state. Recomputing with
      // the same two helpers keeps a retry on the term the operator originally
      // picked; without it the retry would drop the term key and land back on
      // whichever term is in force today.
      // Same shape decision as handleLaunch — this path serves the whole
      // catalog too, and the retry is the one a stressed AM actually hits. The
      // split comes from the SAME `splitLaunchValues`, so a retry cannot
      // disagree with the launch about what an action is.
      const binding = buildLaunchBinding(catalogEntry.input_schema ?? null);
      const { runLevelValues, actions } = splitLaunchValues(binding, values);
      const retryFlat = mergeLaunchSlice(binding, runLevelValues, actions[0]);
      const retryTerm = findPolicyOption(
        readPolicyOptions(retryFlat),
        typeof retryFlat.policy_number === "string"
          ? retryFlat.policy_number.trim()
          : null,
        readPolicyTermKey(retryFlat),
      );
      const retryLauncherInputs: Record<string, unknown> = {
        ...(clientSkProp != null ? { client_sk: clientSkProp } : {}),
        ...(clientNameProp ? { client_name: clientNameProp } : {}),
        // The identity fields are hidden silent-capture inputs — never in the
        // SchemaForm `values`, so re-inject them on retry too, through the
        // SAME producer seam handleLaunch uses. A retry that dropped
        // `internet_message_id` would be refused by the substrate on any
        // workflow that declares it (AC-7).
        ...buildLaunchIdentityInputs(
          { internetMessageId, graphMessageId },
          {
            requireIdentity: launchRequiresMessageIdentity(
              catalogEntry.input_schema ?? null,
            ),
          },
        ),
        // Preserve the SP-mode choice on retry (gated by shouldShowSpToggle).
        ...(shouldShowSpToggle(catalogEntry) ? { use_sp_login: useSpLogin } : {}),
        idempotency_key: newKey,
      };
      // Through the SAME assembler as handleLaunch (plan Phase 3), so the
      // retry cannot drift from the launch it is retrying. On the reshaped
      // contract the term identity is READ BACK out of `values` (it lives on
      // the action the term control wrote) rather than re-derived into
      // run-level inputs — which is also what removes the retry's old
      // dependence on `policy_options` still being present in the form
      // state. NOTE: `retryTerm` is derived directly above (not via
      // `resolveLaunchTerm`) — a retry has never run the policy-membership /
      // effective-date guards, and `resolveLaunchTerm` bundles them; reusing
      // it here would newly block a retry that today always proceeds.
      const assembledRetry = assembleLaunchInputs({
        binding,
        next: values,
        injected: retryLauncherInputs,
        chosenTerm: retryTerm,
      });
      if (assembledRetry.dropped.length > 0) {
        logger.warn("InstanceDetailDrawer: retry dropped undeclared keys", {
          workflow_id: catalogEntry.id,
          dropped: assembledRetry.dropped,
        });
      }
      const inputs = assembledRetry.inputs;
      const result = await orchestratorApi.triggerWorkflow(
        catalogEntry.id,
        inputs,
      );
      onRetried?.({
        instance_id: result.instance_id,
        workflow_id: result.workflow_id,
        idempotency_key: newKey,
      });
    } catch (err) {
      logger.warn("InstanceDetailDrawer: retry failed", {
        workflow_id: catalogEntry.id,
        error: String(err),
      });
    } finally {
      setRetrying(false);
    }
  }, [catalogEntry, previousIdempotencyKey, values, onRetried, clientSkProp, clientNameProp, useSpLogin, graphMessageId, internetMessageId, runLaunchableInPlugin]);

  // Instance id — surfaced as its own copy link so triage starts from the
  // durable workflow_instances.instance_id (matches DB rows, DT history,
  // MLflow trace_id, SignalR group name). The correlation_id fallback in
  // handleCopyCorrelationId is only useful when an error response carried
  // one; otherwise it also returns the instance id, which is confusing.
  const handleCopyInstanceId = useCallback(() => {
    const id = instanceId ?? "";
    if (id && typeof navigator !== "undefined" && navigator.clipboard) {
      void navigator.clipboard.writeText(id);
    }
  }, [instanceId]);

  const handleCopyCorrelationId = useCallback(() => {
    const cid = (push.lastError as { correlation_id?: string } | null)
      ?.correlation_id;
    const id = cid ?? instanceId ?? "";
    if (id && typeof navigator !== "undefined" && navigator.clipboard) {
      void navigator.clipboard.writeText(id);
    }
  }, [push.lastError, instanceId]);

  // Exec-plane session token, surfaced from the persisted hitl_question
  // payload (substrate sets q["page_id"] on every HITL question it builds).
  // Falls back to the durable last_page_id column (substrate migration 024)
  // so the operator can still grab the token between HITL pauses, after the
  // substrate has cleared hitl_question on resume/cancel.
  const sessionId = useMemo<string | null>(() => {
    const q = push.status?.hitl_question as { page_id?: unknown } | null | undefined;
    const pid = q?.page_id;
    if (typeof pid === "string" && pid.length > 0) return pid;
    const last = push.status?.last_page_id;
    return typeof last === "string" && last.length > 0 ? last : null;
  }, [push.status?.hitl_question, push.status?.last_page_id]);

  const handleCopySessionId = useCallback(() => {
    if (sessionId && typeof navigator !== "undefined" && navigator.clipboard) {
      void navigator.clipboard.writeText(sessionId);
    }
  }, [sessionId]);

  const [killingSession, setKillingSession] = useState(false);
  const handleKillSession = useCallback(async () => {
    if (!instanceId || killingSession) return;
    setKillingSession(true);
    try {
      await orchestratorApi.killExecPlaneSession(instanceId, sessionId ?? undefined);
    } catch (err) {
      logger.warn("InstanceDetailDrawer: killExecPlaneSession failed", {
        instance_id: instanceId,
        error: String(err),
      });
    } finally {
      setKillingSession(false);
    }
  }, [instanceId, sessionId, killingSession]);

  // The entry that DESCRIBES THE RUN, as opposed to `catalogEntry`, which is
  // the one the caller picked to LAUNCH. They are different questions and only
  // this one can be answered for every run: opening from an ora notification
  // or from the notification card's `InvokeAddInCommand` hands the pane an
  // instance and no workflow id, so `catalogEntry` is null there — and with it
  // the whole declared manifest, which is what made the Steps log claim every
  // step of 81363a33 had executed. Resolved through the SAME helper ora uses.
  //
  // Deliberately NOT folded back into `catalogEntry`: every launch gate reads
  // that prop, and a run-resolved entry would quietly arm launch surfaces the
  // caller never opened.
  // Full or null — resolved and completed at the top (2026-10-06).
  const displayCatalogEntry = runEntry.entry;

  // The phase list the pipeline and the chip render, from the shared builder —
  // the same call ora makes. This block used to be a second fallback chain with
  // its own precedence, and it normalized the raw catalog kind through
  // `kindToPhaseType` into a domain-badge category, which is why the plugin's
  // step tooltip said "human" where ora said "Human review".
  const synthesizedPhases: PhaseDefinition[] = useMemo(
    () =>
      buildPhaseDefinitions({
        explicitPhases: phases,
        catalogEntry: displayCatalogEntry,
        status: push.status,
      }),
    [phases, displayCatalogEntry, push.status],
  );

  const phaseStatuses = useMemo(
    () => derivePhaseStatusesFromInstance(synthesizedPhases, push.status),
    [synthesizedPhases, push.status],
  );

  // A compact entry carries the name, so the title never waits on the read.
  // With no entry at all, the shared display-name resolver (never a
  // title-cased dispatch key: "Mortgagee Change", story 28633).
  const titleEntry = displayCatalogEntry ?? catalogEntryProp ?? runSeed;
  const drawerTitle =
    titleEntry?.name ??
    titleEntry?.description ??
    workflowDisplayName(push.status?.workflow_id ?? titleEntry?.id ?? null) ??
    "Workflow";

  // ONE way back, always at the top (Joachim, 2026-10-06: "only show me the
  // one that takes me to the previous screen ... OK to click two times").
  // More information open -> it steps back to the run; otherwise it leaves
  // the drawer, named by the host. No onBack (no host label) still gets one -
  // with Close gone it is the only visible exit. Esc does the same.
  // Story 28633: it is the brand bar's "← Back"; `label` is its ACCESSIBLE
  // name (the destination), the visible text is always "Back".
  const backRow = moreInfoOpen
    ? { label: `Back to ${drawerTitle}`, onBack: onMoreInfoClose }
    : { label: backLabel ?? "Back", onBack: onBack ?? onClose };

  // Warning acknowledgement (2026-10-06): ONE live state for the header pill,
  // the Done view and the terminal body's warning lines. Only server-confirmed
  // acknowledgements clear a line (R50).
  const warningAck = useWarningAcknowledgements({
    instanceId: instanceId ?? null,
    status: push.status,
    acknowledge: (iid, wid) => orchestratorApi.acknowledgeRunWarning(iid, wid),
  });
  const acknowledgedStatus = useMemo(
    () => withAcknowledgements(push.status, warningAck.items),
    [push.status, warningAck.items],
  );
  // R89/R65: ONE pill value for the header AND the run record. The hook reports
  // `false` for an empty line list (server-confirmed "no lines"), but a body that
  // still shows a legacy warning must never sit under a plain "Completed": with
  // no lines to acknowledge, unknown (null) gives the legacy "*".
  const legacyWarnings = statusCarriesWarnings(push.status);
  const rawPillAck = pillNeedsAcknowledgement({ status: push.status, live: warningAck.needsAcknowledgement });
  // I1: not only for an empty list -- ANY legacy text the body still shows (its
  // own stand-down rules) keeps the pill off a plain "Completed".
  // The GET /result body is fetched HERE (not in TerminalBody): a legacy warning
  // that only the result body carries must reach the pill in the same render, with
  // no frame of green "Completed" in between.
  const resultData = useRunResultBody(instanceId ?? null, open && effectiveMode === "terminal");
  const legacyShown = legacyWarningsShown(push.status, warningAck.items, resultData as Record<string, unknown> | null);
  // Lines present on the status that the hook could not read: never plain green.
  const unreadableRawLines =
    warningAck.items === null &&
    Array.isArray((push.status as { warning_items?: unknown } | null)?.warning_items) &&
    ((push.status as { warning_items?: unknown[] }).warning_items?.length ?? 0) > 0 &&
    readWarningItems(push.status) === null;
  // ONE value for the header pill AND the run record's badge.
  const pillHasWarnings = legacyWarnings || legacyShown.any || unreadableRawLines;
  const pillNeedsAck =
    rawPillAck === false &&
    (legacyShown.any || unreadableRawLines || ((warningAck.items?.length ?? 0) === 0 && legacyWarnings))
      ? null
      : rawPillAck;
  const pill = terminalStatusPill(push.status?.status, pillNeedsAck, pillHasWarnings);
  // The run's comfort level, as the AM picked it ("Comfort: High" for LOW).
  const runComfortLabel = comfortLabel(push.status?.oversight_level);
  const finalStatus = (push.status?.status ?? "").toLowerCase();
  const failed = finalStatus === "failed";
  const cancelled =
    finalStatus === "cancelled" || finalStatus === "timeout_cancelled";

  // Retry on a failed run. Why it is greyed out is ora's tooltip, not a
  // native title (story 28633): the span is the hover target (a disabled
  // button fires no pointer events), and node title + describeChild put no
  // aria-label or native title on it; a null title never opens. Always
  // wrapped, so the button is not remounted when the entry finishes loading.
  const retryBlockedReason = !runLaunchableInPlugin
    ? "This workflow can't be started from the Outlook pane, so it can't be retried here. Start it from ora."
    : launchEntry.status === "loading"
      ? "Still loading this workflow's details…"
      : launchEntry.status === "error"
        ? "This workflow's details haven't loaded yet — use Try again above."
        : !catalogEntry
          ? "This workflow's details aren't available to retry from here."
          : null;
  const retryAction = (
    <CustomTooltip
      id="retry-button"
      title={retryBlockedReason ? <span>{retryBlockedReason}</span> : null}
      describeChild
      placement="top"
      arrow
      isOnModal
    >
      <span className="inline-flex">
        <button
          type="button"
          onClick={handleRetry}
          disabled={retrying || !catalogEntry || !runLaunchableInPlugin}
          className="px-3 py-1 flex justify-center items-center rounded bg-sunrise text-primary-gray-light-900 text-xs font-semibold hover:bg-blossom transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="retry-button"
        >
          {retrying ? "Retrying…" : "Retry"}
        </button>
      </span>
    </CustomTooltip>
  );

  return (
    <PaneTakeoverDrawer open={open} onClose={backRow.onBack}>
      <MoreInformationOwnBackRowContext.Provider value={false}>
      <div className="flex h-full min-h-0 flex-col" data-testid="instance-drawer">
        {/* ── Brand bar with the ONLY exit ─────────────────────────────
            Story 28633: the separate back row moved into the brand bar to save
            a row on every page. Visible text is always "← Back"; the
            destination ("Back to your queue", or the run while More
            information is open) is its accessible name. This drawer is
            `height: 100vh`, so while it is open the pane's tab bar is covered
            and this is the only signposted way out. */}
        <WorkflowCenterBar
          logoSrc={oraLogoUrl()}
          onBack={backRow.onBack}
          backAriaLabel={backRow.label}
          backTestId="drawer-back"
        />
        {/* Header — ONE line, `title · Started 5 min ago` (story 28633), the
            SHARED takeover chrome. Live/terminal only: on the launch page the
            title row sits UNDER the client box, as the form's own heading
            (PreLaunchBody). */}
        {effectiveMode !== "pre_launch" && (
          <TakeoverTitleHeader
            inline
            title={drawerTitle}
            subtitle={
              push.status?.created_at
                ? `Started ${formatRelativeTime(push.status.created_at)}`
                : effectiveMode === "live"
                  ? "Starting…"
                  : "—"
            }
            rightSlot={
              <>
              {pill && effectiveMode === "terminal" && (
                // The pill's explanation (e.g. the warnings to acknowledge) in
                // ora's tooltip, not a native title (story 28633). Node title
                // + describeChild: the pill's own name is untouched; a null
                // title never opens.
                <CustomTooltip
                  id="status-pill"
                  title={pill.title ? <span>{pill.title}</span> : null}
                  describeChild
                  placement="bottom-end"
                  arrow
                  isOnModal
                >
                  <span
                    className={[
                      "text-2xs font-semibold px-1.5 py-[1px] rounded",
                      pill.bg,
                      pill.text,
                    ].join(" ")}
                    aria-label={pill.ariaLabel ?? undefined}
                    data-testid="status-pill"
                  >
                    {pill.label}
                    {pill.ariaLabel && (
                      // A bare span's aria-label is not reliably announced: say it in
                      // text too (as ora's WorkflowStatusBadge does).
                      <span className="sr-only">
                        {" "}
                        {pill.ariaLabel.slice(pill.label.replace(/\*$/, "").length).trim()}
                      </span>
                    )}
                  </span>
                </CustomTooltip>
              )}
              {/* Comfort level — read-only, shown on live + terminal for audit (AC5).
                  Reads oversight_level from the run status (cached per-run by
                  read_user_oversight_level at run-start) and shows it as the
                  COMFORT label the AM picked it by (story 28633: LOW -> "High",
                  NONE -> "Full"); the wire value is unchanged. The tooltip is
                  ora's CustomTooltip, not a native title (node title +
                  describeChild keep "Comfort: High" as the badge's text). */}
              {(effectiveMode === "live" || effectiveMode === "terminal") &&
                runComfortLabel && (
                  <CustomTooltip
                    id="oversight-level-badge"
                    title={<span>Comfort level set at run start</span>}
                    describeChild
                    placement="bottom-end"
                    arrow
                    isOnModal
                  >
                    <span
                      className="text-2xs font-medium text-primary-gray-light-500 bg-primary-gray-light-100 border border-primary-gray-light-200 px-1.5 py-[1px] rounded whitespace-nowrap"
                      data-testid="oversight-level-badge"
                    >
                      Comfort: {runComfortLabel}
                    </span>
                  </CustomTooltip>
                )}
              </>
            }
          />
        )}

        {/* Body — min-h-0 is required: without it the flex item cannot shrink
            below its content height so overflow-y-auto never activates.
            `relative` here (live/terminal only) is deliberate: it's the
            positioning ancestor for MoreInformationSection's `absolute
            inset-0` overlay. It must live on THIS non-scrolling wrapper, not
            on live-body/terminal-body below (which has `overflow-y-auto`) —
            an absolutely-positioned overlay inside a scrolling positioned
            ancestor scrolls out of view along with everything else the
            moment the AM has scrolled before opening it. */}
        <div
          className={
            // The launch page is tighter (story 28633: no scrolling in a
            // ~440×720 pane); live/terminal keep their p-4.
            effectiveMode === "pre_launch"
              ? "flex min-h-0 flex-1 flex-col overflow-hidden px-3 py-2"
              : "relative flex min-h-0 flex-1 flex-col overflow-hidden p-4"
          }
        >
          {/* Phase 2: cross-client terminal banner. Renders only when push
              observed an external (not locally initiated) terminal status
              transition. The user manually dismisses; the drawer's
              terminal body underneath continues to render the final
              state (frozen pipeline + artifacts + result). */}
          {showFinishedElsewhereBanner && (
            <div className="mb-3">
              <FinishedElsewhereBanner
                status={
                  terminalStatusKey as
                    | "completed"
                    | "cancelled"
                    | "timeout_cancelled"
                    | "failed"
                }
                warning={Boolean(pill?.label.endsWith("*"))}
                onDismiss={() => setBannerDismissed(true)}
              />
            </div>
          )}
          {effectiveMode === "pre_launch" && (
            <PreLaunchBody
              catalogEntry={catalogEntry}
              entryStatus={launchEntry.status}
              onRetryEntry={launchEntry.retry}
              clientSk={clientSkProp}
              values={values}
              setValues={setValues}
              renderInputForm={renderInputForm}
              renderClientSection={renderClientSection}
              fieldConfidence={fieldConfidence}
              prefillLoading={prefillLoading}
              submitting={submitting}
              submitError={submitError}
              autoLaunching={autoLaunching}
              workflowName={drawerTitle}
              oversightLevel={oversightLevel}
              onOversightLevelChange={handleOversightLevelChange}
              useSpLogin={useSpLogin}
              onUseSpLoginChange={setUseSpLogin}
              spCredsReady={spCredsReady}
              spReadinessChecking={spReadinessChecking}
              onRecheckSpReadiness={recheckSpReadiness}
              onSubmit={handleLaunch}
            />
          )}

          {/* Concurrent-run confirmation. Replaces the old hard block: the
              operator is told WHICH run is already in flight and what state it
              is in, then chooses. Continue replays the same values with an
              acknowledgement scoped to that instance. */}
          {concurrentPrompt && (
            <ConcurrentRunConfirm
              instance={concurrentPrompt.instance}
              onCancel={() => setConcurrentPrompt(null)}
              onContinue={() => {
                const { instance, values: pending } = concurrentPrompt;
                setConcurrentPrompt(null);
                void handleLaunch(pending, instance.instance_id);
              }}
            />
          )}

          {(effectiveMode === "live" || effectiveMode === "terminal") && (() => {
            // Derive client SK from the workflow's own status data — shared by
            // both live and terminal modes. Check inputs then metadata then prop.
            const rawFromInputs = push.status?.inputs?.client_sk;
            const skFromInputs =
              typeof rawFromInputs === "number"
                ? rawFromInputs
                : typeof rawFromInputs === "string" && rawFromInputs
                  ? Number(rawFromInputs) || null
                  : null;
            const liveCtx = (push.status?.metadata as Record<string, unknown> | undefined)
              ?.client_context as Record<string, unknown> | undefined;
            const rawFromMeta = (liveCtx?.client as Record<string, unknown> | undefined)?.sk;
            const skFromMeta =
              typeof rawFromMeta === "number"
                ? rawFromMeta
                : typeof rawFromMeta === "string" && rawFromMeta
                  ? Number(rawFromMeta) || null
                  : null;
            const clientSk = skFromInputs ?? skFromMeta ?? clientSkProp ?? null;

            // ONE stream node, handed to BOTH inner trees. The grouped
            // manifest comes from the CATALOG entry, not a locally assembled
            // one.
            // Built ONCE even when both flags are on: `buildPhaseStream`
            // emits a fallback telemetry event for a pre-model instance, and a
            // second call would double-count it.
            // While the run's full entry is being read (or the read failed),
            // the step list is a status line, never a stream built without
            // the manifest — and buildPhaseStream is not called, so the wait
            // records no spurious pre-model fallback (2026-10-06).
            const runEntryStatus = runEntry.status;
            const phaseStreamModel =
              runEntryStatus === "loading" || runEntryStatus === "error"
                ? null
                : buildPhaseStream(push.status, displayCatalogEntry ?? null);
            const phaseStream =
              runEntryStatus === "loading" || runEntryStatus === "error" ? (
                <CatalogEntryNotice
                  status={runEntryStatus}
                  what="steps"
                  onRetry={runEntry.retry}
                />
              ) : phaseStreamModel ? (
                <PhaseMilestoneStream model={phaseStreamModel} />
              ) : null;

            // running-drawer: the submitted request. Written into BOTH maps —
            // a live-only write leaves completed / failed / cancelled runs with
            // no submitted request, which AC-9 ("including terminal runs")
            // forbids. This is the dual-tree defect the add-in is prone to.
            const submittedRequestModel = readSubmittedRequest(push.status);
            const requestSection = submittedRequestModel ? (
              <SubmittedRequestSection
                model={submittedRequestModel}
                onReuse={runLaunchableInPlugin ? onReuse : undefined}
              />
            ) : null;

            // running-drawer AC-7: the detailed chronological log, rendered
            // ONLY inside the more-information entry point. Built once here and
            // handed to BOTH inner trees, like `phaseStream`.
            //
            // Every input is derived inside the shared `RunStepsLog` container.
            // This block used to assemble the chronology read, the declared
            // manifest, the live pointer and the failed-status vocabulary
            // itself, and ora's drawer assembled its own copy — which is how
            // one run rendered 17 never-run steps in ora and 45 all-green rows
            // here. The manifest in particular came off the `catalogEntry`
            // PROP, absent on both notification landings, and an empty
            // manifest is exactly what makes the log stop reporting the steps
            // that never ran. Nothing about the Steps view lives here now.
            const detailLogIsTerminal = effectiveMode === "terminal";
            // running-drawer: copy-id links + the SignalR ping diagnostic,
            // moved behind the more-information entry point (previously
            // always-visible in the scrollable body). Flag-off keeps them in
            // their original spot — see the inline SignalR block below and
            // DrawerFooter's `showCopyRow`.
            const debugActions = (
              <DebugActionsPanel
                instanceId={instanceId}
                onCopyInstance={handleCopyInstanceId}
                onCopyCorrelation={handleCopyCorrelationId}
                sessionId={sessionId}
                onCopySession={handleCopySessionId}
                onKillSession={handleKillSession}
                isLiveConnected={ownPush.isLiveConnected}
                pingMessages={pingMessages}
                pingSending={pingSending}
                onSendPing={sendPing}
              />
            );
            // The run inputs behind "More information". The parent is the only
            // scope holding the catalog, and RunStepsLog -- not either body --
            // derives chronology/manifest/pointer from them. That split is the
            // fix recorded above: assembling these per host is what made one
            // run render 17 never-run steps in ora and 45 all-green rows here.
            const runSteps: RunEvidenceStepsInput = {
              status: push.status,
              // The FULL entry only, never the list: RunStepsLog falls back to
              // the list when the entry has no steps, and a compact entry has
              // none — which is the 81363a33 all-green log (2026-10-06).
              catalog: displayCatalogEntry ? [displayCatalogEntry] : null,
              catalogEntry: displayCatalogEntry,
              isTerminal: detailLogIsTerminal,
            };

            // - the step view (story 26739) -------------------------
            // ONE node, handed to BOTH inner trees - the dual-tree rule the
            // running-drawer work is documented on. Everything it reads comes
            // from `push.status`, so a terminal push re-derives it without a
            // remount and with no state to go stale.
            const stepViewModel = stepViewsEnabled
              ? buildStepViewModel({
                  status: acknowledgedStatus,
                  hitlQuestion: push.status?.hitl_question ?? null,
                  fieldConfidence,
                  clientNameFallback: clientNameProp,
                })
              : null;
            // The Done view OWNS the warning lines when it is on screen (2026-10-06,
            // Joachim saw the early-exit reason and "acknowledged by" twice); the
            // body's own list steps aside (TerminalBody `doneOwnsWarningLines`).
            const doneWarningLines =
              stepViewName === "done" && (warningAck.items?.length ?? 0) > 0 ? (
                <WarningAcknowledgementList ack={warningAck} />
              ) : null;
            const stepView: React.ReactNode | null =
              !stepViewsEnabled || !stepViewModel || !stepViewName ? null : stepViewName ===
                "brief" ? (
                <BriefApproveView
                  clientName={stepViewModel.clientName}
                  policyNumber={stepViewModel.policyNumber}
                  resolvedFields={stepViewModel.resolvedFields}
                  changeLead={stepViewModel.changeLead}
                  diffRows={stepViewModel.diffRows}
                />
              ) : stepViewName === "running" ? (
                <RunningView
                  // Operator directive 2026-09-16: the phase stream directly
                  // ABOVE this view is the one status surface. It already
                  // states the run's position and the header already states
                  // the client and policy, so this view keeps only what was
                  // additive — "safe to close" and the held-able countdown.
                  // `identityLine(null, null)` is null and StepViewFrame skips
                  // an absent headline/subline, so nothing is duplicated.
                  clientName={null}
                  policyNumber={null}
                  // Same rule as ora: the sentence survives ONLY for the beat
                  // before the status arrives, when the stream has nothing to
                  // say. After that the stream states the position.
                  sectionLabel={
                    !stepViewDataReady
                      ? sectionLabelFor(
                          phaseStreamModel,
                          push.status?.status ?? null,
                          readCatalogSections(displayCatalogEntry),
                        )
                      : null
                  }
                  // Same gate as the countdown below, for the same reason: a
                  // null status dispatches here too, and neither a "Running"
                  // pill nor "we'll call you when we need you" is a thing to
                  // say about a run whose status has not arrived — it may
                  // have finished last week.
                  loading={!stepViewDataReady}
                  // Whatever is LEFT of the deadline, not a fresh budget, so a
                  // refresh cannot restart the digits the Servicer is reading.
                  autoCloseSeconds={stepViewAutoCloseSeconds}
                  // Holding clears the deadline, so the seconds above go null;
                  // without this the row would simply disappear and the AM
                  // would be left guessing whether the pane still intends to
                  // close on them.
                  autoCloseHeld={stepViewAutoCloseHeld}
                  onHoldAutoClose={() => {
                    // Releases BOTH clocks - the digits the Servicer is
                    // reading and the timeout underneath them - by clearing
                    // the one deadline they share.
                    setStepViewAutoCloseHeld(true);
                  }}
                />
              ) : stepViewName === "exception" ? (
                <ExceptionView
                  clientName={stepViewModel.clientName}
                  policyNumber={stepViewModel.policyNumber}
                  // D2, PROPOSED: there is no before/after on a HITL payload,
                  // so "what it says now" is the interest the run named as its
                  // target and "what the email asks for" is the draft. Absent
                  // either, the row is not drawn - never filled with a guess.
                  currentValue={stepViewModel.targetInterest}
                  requestedValue={
                    stepViewModel.diffRows.find((r) => r.label === "Mortgagee")
                      ?.after ?? null
                  }
                  // The hop back to the lender email lives in the drawer
                  // header on every view, this one included; a second copy
                  // in "Launched from" would be the same control twice.
                  sourceRequestRow={null}
                  // The options live on the shared DecisionCard below, which
                  // owns the submit latch. A second pill row here would be a
                  // second submit path around it.
                  hitlQuestion={null}
                />
              ) : stepViewName === "carrier_packet" ? (
                <CarrierPacketView
                  clientName={stepViewModel.clientName}
                  policyNumber={stepViewModel.policyNumber}
                  packetRows={stepViewModel.packetRows}
                  portalUrl={stepViewModel.portalUrl}
                  // No uploader here: the body's decision/status region below
                  // owns THE evidence uploader, and there is exactly one.
                  evidencePanel={null}
                  // Waiting on the CARRIER's documents is the white "Pending
                  // Documents"; on the Servicer's OWN, Needs You (story 28633).
                  pendingDocuments={isWaitingOnCarrierDocuments(push.status?.status, {
                    hitlQuestion: push.status?.hitl_question,
                  })}
                />
              ) : stepViewName === "reply_ready" ? (
                <ReplyReadyView
                  clientName={stepViewModel.clientName}
                  policyNumber={stepViewModel.policyNumber}
                  proofSentence={stepViewModel.proofSentence}
                  warningSentence={stepViewModel.replyWarning}
                  bullets={stepViewModel.bullets}
                  onOpenProof={() => {
                    emitTouchpoint("result_review", "arrive", {
                      instance_id: instanceId,
                    });
                    setMoreInfoOpen(true);
                  }}
                  onOpenDraft={
                    stepViewModel.draftMessageId
                      ? () => {
                          emitTouchpoint("reply_send", "arrive", {
                            instance_id: instanceId,
                          });
                          openMessageInOutlook(stepViewModel.draftMessageId ?? "");
                        }
                      : undefined
                  }
                  openDraftHint={
                    stepViewModel.draftMessageId
                      ? null
                      : "This run did not record a reply draft - open your Outlook Drafts folder."
                  }
                />
              ) : (
                <DoneView
                  clientName={stepViewModel.clientName}
                  policyNumber={stepViewModel.policyNumber}
                  sentence={
                    doneWarningLines
                      ? doneSentenceBesideLines(stepViewModel.doneSentence, warningAck.items)
                      : stepViewModel.doneSentence
                  }
                  endedEarly={stepViewModel.endedEarly}
                  outcome={stepViewModel.outcome}
                  acknowledged={stepViewModel.acknowledged}
                  warningLines={doneWarningLines}
                />
              );

            if (effectiveMode === "live") {
              return (
                <LiveBody
                  push={push}
                  warningAck={warningAck}
                  phases={synthesizedPhases}
                  phaseStatuses={phaseStatuses}
                  instanceId={instanceId ?? null}
                  clientSk={clientSk}
                  fieldConfidence={fieldConfidence}
                  clientName={clientNameProp}
                  openItemMessageId={graphMessageId}
                  hitlQuestion={push.status?.hitl_question ?? null}
                  artifacts={artifacts}
                  artifactsLoading={artifactsLoading}
                  onArtifactClick={handleArtifactClick}
                  fetchArtifactPayload={fetchArtifactPayload}
                  onEvidenceInteract={holdStepViewAutoClose}
                  onClaimedAutoClose={onAutoClose ?? onClose}
                  hitlEvents={hitlEvents}
                  hitlLoading={hitlLoading}
                  onHitlResponded={() => {
                    hitlSubmittedRef.current = true;
                    // Nudge the pinned pane's queue immediately — see the
                    // prop doc. Fired here, at submit, not at drawer close:
                    // the AM may keep the drawer open, and the queue should
                    // not wait on that.
                    onHitlRespondedProp?.();
                    // Burst-poll for ~20s after the user submits. The 60s
                    // safety heartbeat is too slow to catch the substrate's
                    // post-resume state transition when SignalR doesn't fire
                    // a status_changed event for it — and during that window
                    // the user sees a disabled approve pill + stale step
                    // pipeline ("the process feels really slow"). Once the
                    // status flips off paused_hitl, the ReviewCheckpointPanel
                    // unmounts and the user sees the workflow progress.
                    //
                    // `authoritative: true` bypasses mergeStatus's
                    // HITL-beats-non-HITL race protection — the user just
                    // told the substrate to advance, so the server response
                    // IS the truth. Without this, after the substrate flips
                    // to running, the merge would keep the stale warm_hitl
                    // state (because warm_hitl beats running) and the panel
                    // would stay up with the old question.
                    void ownPush.refetch({ authoritative: true });
                    const ladder = [1_000, 3_000, 6_000, 10_000, 20_000];
                    ladder.forEach((delay) => {
                      setTimeout(() => {
                        void ownPush.refetch({ authoritative: true });
                      }, delay);
                    });
                  }}
                  phaseStream={phaseStream}
                  stepView={stepView}
                  stepViewName={stepViewName}
                  requestSection={requestSection}
                  debugActions={debugActions}
                  runSteps={runSteps}
                  moreInfoOpen={moreInfoOpen}
                  onMoreInfoClose={onMoreInfoClose}
                />
              );
            }

            return (
              <TerminalBody
                push={push}
                warningAck={warningAck}
                doneOwnsWarningLines={Boolean(stepView && doneWarningLines)}
                pillNeedsAck={pillNeedsAck}
                pillHasWarnings={pillHasWarnings}
                resultData={resultData}
                openItemMessageId={graphMessageId}
                phases={synthesizedPhases}
                phaseStatuses={phaseStatuses}
                artifacts={artifacts}
                artifactsLoading={artifactsLoading}
                onArtifactClick={handleArtifactClick}
                fetchArtifactPayload={fetchArtifactPayload}
                hitlEvents={hitlEvents}
                hitlLoading={hitlLoading}
                instanceId={instanceId ?? null}
                clientSk={clientSk}
                clientName={clientNameProp}
                isEndorsement={isEndorsementFamily(
                  push.status?.workflow_id ?? catalogEntry?.id ?? null,
                )}
                failed={failed}
                cancelled={cancelled}
                phaseStream={phaseStream}
                stepView={stepView}
                stepViewName={stepViewName}
                requestSection={requestSection}
                debugActions={debugActions}
                runSteps={runSteps}
                moreInfoOpen={moreInfoOpen}
                onMoreInfoClose={onMoreInfoClose}
              />
            );
          })()}

        </div>
        {/* Sticky footer — a DIRECT child of the drawer frame, deliberately
            OUTSIDE the padded/overflow-hidden mode-body wrapper above. ora
            does the same (CustomDrawer's `footer` slot sits outside its
            scrolling body), and that placement is what the layout depends on:
            inside the wrapper the row inherits its `p-4`, so the border-t
            stops short of both edges and the buttons carry double padding
            (wrapper p-4 + the row's own px-4). Owning it here rather than in
            LiveBody/TerminalBody also keeps it visible while the
            More-information pane covers the body — again matching ora. */}
        {(effectiveMode === "live" || effectiveMode === "terminal") && (
          <DrawerFooter
            // Resets the footer's local confirmingCancel state when the pane
            // is pointed at a different run (ora does this too).
            key={instanceId ?? "no-instance"}
            density="compact"
            instanceId={instanceId}
            onCopyInstance={handleCopyInstanceId}
            onCopyCorrelation={handleCopyCorrelationId}
            sessionId={sessionId}
            onCopySession={handleCopySessionId}
            onKillSession={handleKillSession}
            canCancel={effectiveMode === "live" && !!instanceId}
            isActionInFlight={cancelling || killingSession}
            onCancel={handleConfirmCancel}
            onClose={onClose}
            showClose={false}
            showCopyRow={false}
            onOpenMoreInformation={
              () => setMoreInfoOpen(true)
            }
            moreInformationOpen={moreInfoOpen}
            extraActions={
              effectiveMode === "terminal" && failed && instanceId
                ? retryAction
                : null
            }
          />
        )}
      </div>
      {artifactLightbox}
      </MoreInformationOwnBackRowContext.Provider>
    </PaneTakeoverDrawer>
  );
}

export default InstanceDetailDrawer;

// ---------------------------------------------------------------------------
// pre_launch body
// ---------------------------------------------------------------------------

function PreLaunchBody({
  catalogEntry,
  entryStatus,
  onRetryEntry,
  clientSk,
  values,
  setValues,
  renderInputForm,
  renderClientSection,
  fieldConfidence,
  prefillLoading,
  submitting,
  submitError,
  autoLaunching,
  workflowName,
  oversightLevel,
  onOversightLevelChange,
  useSpLogin,
  onUseSpLoginChange,
  spCredsReady,
  spReadinessChecking,
  onRecheckSpReadiness,
  onSubmit,
}: {
  catalogEntry?: WorkflowCatalogEntry | null;
  /** Where the read of the launch entry's FULL form stands (2026-10-06):
   *  "loading" / "error" render a status line in place of the form. */
  entryStatus: FullCatalogEntry["status"];
  onRetryEntry: () => void;
  clientSk?: number | null;
  values: Record<string, unknown>;
  /** The drawer's own setter — passed straight through to `renderInputForm`,
   *  whose declared signature accepts a functional update. */
  setValues: Dispatch<SetStateAction<Record<string, unknown>>>;
  renderInputForm?: InstanceDetailDrawerProps["renderInputForm"];
  renderClientSection?: InstanceDetailDrawerProps["renderClientSection"];
  fieldConfidence?: Record<string, "high" | "medium" | "low">;
  prefillLoading?: boolean;
  submitting: boolean;
  submitError: string | null;
  autoLaunching: boolean;
  /** The workflow's display name, for the launch page's one-line title. */
  workflowName: string;
  oversightLevel: OversightLevel;
  onOversightLevelChange: (v: OversightLevel) => void;
  useSpLogin: boolean;
  onUseSpLoginChange: (v: boolean) => void;
  spCredsReady: boolean | null;
  /** A probe is in flight — Retry reads "Checking…" meanwhile. */
  spReadinessChecking: boolean;
  /** Re-run the readiness probe (Retry, or Undo back to the default). */
  onRecheckSpReadiness: () => void;
  onSubmit: (next: Record<string, unknown>) => void;
}) {
  const schema = useMemo(
    () =>
      withoutHostDrawnSignIn(
        (catalogEntry?.input_schema as Record<string, unknown> | undefined) ?? null,
        shouldShowSpToggle(catalogEntry),
      ),
    [catalogEntry],
  );

  const schemaProps = schema
    ? (schema as { properties?: Record<string, unknown> }).properties
    : null;
  const fieldLoading: Record<string, boolean> | undefined =
    prefillLoading && schemaProps
      ? Object.fromEntries(Object.keys(schemaProps).map((k) => [k, true]))
      : undefined;

  // The launch page's one-line title (story 28633). It sits UNDER the client
  // box — the order is brand bar, client, this, the form — so it is part of
  // the body, not the takeover chrome.
  // The full title when it truncates, in ora's tooltip (story 28633).
  const titleRow = (text: string) => (
    <CustomTooltip
      id="prelaunch-title"
      title={text}
      placement="bottom-start"
      arrow
      isOnModal
    >
      <h2
        className={`${TYPE_TITLE} flex-shrink-0 truncate text-primary-gray-light-900`}
        data-testid="prelaunch-title"
      >
        {text}
      </h2>
    </CustomTooltip>
  );
  const confirmTitle = `Confirm ${workflowName} details & submit`;

  if (!catalogEntry) {
    // A compact entry's full read is in flight or failed: never a form without
    // its schema (plugin catalog round, 2026-10-06).
    if (entryStatus === "loading" || entryStatus === "error") {
      return (
        <div className="flex flex-col gap-2">
          {titleRow(confirmTitle)}
          <CatalogEntryNotice status={entryStatus} what="form" onRetry={onRetryEntry} />
        </div>
      );
    }
    return (
      <p className="text-sm text-primary-gray-light-600">
        No workflow selected.
      </p>
    );
  }

  if (autoLaunching) {
    return (
      <div className="flex flex-col gap-2">
        {titleRow(workflowName)}
        <div className="flex flex-col items-center justify-center gap-3 py-10" data-testid="prelaunch-launching">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-gray-light-200 border-t-sunrise" />
          <p className="text-sm text-primary-gray-light-600">Launching…</p>
        </div>
      </div>
    );
  }

  // Client-selection gate (prefill-data-threading-and-gate, AC#3). On a
  // mortgagee-update with no client selected, block the launch step: show a
  // perceivable + screen-reader-announced prompt and disable the launch
  // affordance + the policy selector. Mid-flow deselect (clientSk → null)
  // re-shows the prompt and re-disables launch automatically (this is a pure
  // render of the current clientSk). The authoritative backstop is the
  // handleLaunch guard — this is the UX layer. Scoped to the mortgagee-update
  // ids ONLY so other workflows are never gated (R3).
  const clientMissing =
    requiresClient(catalogEntry) && clientSk == null;
  // While identify/prefill is still running, do not ask the AM to search —
  // the lookup hasn't failed yet. Launch stays disabled until a client lands.
  const clientGateBlocked = clientMissing && !prefillLoading;
  // Launch stays blocked without a client, but that is NOT `submitting`.
  // Folding clientMissing into `submitting` made LaunchForm paint "Starting…"
  // and disable Re-analyse on every relaunch that opened with no client.
  const launchDisabled = submitting || clientMissing;

  const clientGateReason: string | null = clientMissing
    ? "Select a client to continue."
    : null;

  // SP-mode launch precondition. Mirrors the handleLaunch gate so the button
  // is GREYED rather than only rejecting on click — a live Submit that does
  // nothing but print an error reads as a broken button.
  //
  // Kept OUT of `submitting` deliberately: that value is handed to the form
  // as the in-flight launch flag, which disables Re-analyse and relabels the
  // button "Starting…". Client-missing and SP unreadiness are preconditions,
  // not a launch in progress — they ride `submitDisabledReason`.
  //
  // Story 28633: the "Epic sign-in options" disclosure is gone. Runs use the
  // shared pool (or v1.2's dedicated SP) by default and the page says NOTHING
  // about sign-in while that is ready or still being checked. Only when it is
  // NOT available does one amber block appear right above the button, with
  // the one way forward: "Use my own Epic login" for this run (plus Retry).
  // The reason under the button never repeats that block's sentence.
  const spApplies = shouldShowSpToggle(catalogEntry);
  const spPooled = usesSpPool(catalogEntry);
  const spUnavailable = spApplies && useSpLogin && spCredsReady === false;
  const spGateReason: string | null =
    spApplies && useSpLogin && spCredsReady !== true
      ? spCredsReady === null
        ? "Checking Epic sign-in…"
        : "Use your own Epic login, or Retry, to continue."
      : null;

  const launchBlockReason: string | null = clientGateReason ?? spGateReason;

  // Placement: handed to the input form's `preFooter`, so it sits right above
  // the Submit button — sign-in is part of the launch decision, not the
  // payload. Null (nothing rendered) whenever the default sign-in is fine.
  const signInSection = !spApplies ? null : spUnavailable ? (
    <div
      className={`flex flex-col gap-1.5 rounded-md border border-primary-warning-200 bg-primary-warning-50 px-3 py-2 ${TYPE_BODY} text-primary-warning-700`}
      role="status"
      data-testid="epic-signin-fallback"
    >
      <p data-testid="epic-signin-fallback-reason">
        {/* An errored pool check says "isn't available right now" — the same
            words as an empty pool, because neither is the AM's to fix and
            both have the same way forward. */}
        {spPooled
          ? "The shared Epic sign-in isn't available right now."
          : "Your Epic Service Principal sign-in isn't set up."}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onUseSpLoginChange(false)}
          disabled={submitting}
          data-testid="epic-signin-use-own"
          className={`rounded-md border border-primary-warning-300 bg-base-white px-2.5 py-1 ${TYPE_TITLE} text-primary-gray-light-900 hover:bg-primary-gray-light-50 disabled:opacity-60`}
        >
          Use my own Epic login
        </button>
        <button
          type="button"
          onClick={onRecheckSpReadiness}
          disabled={spReadinessChecking}
          data-testid="epic-signin-retry"
          className={`rounded px-1.5 py-1 ${TYPE_BODY} text-primary-warning-700 underline hover:text-primary-warning-800 disabled:no-underline`}
        >
          {spReadinessChecking ? "Checking…" : "Retry"}
        </button>
      </div>
    </div>
  ) : !useSpLogin ? (
    <div
      className={`flex items-center justify-between gap-2 ${TYPE_BODY} text-primary-gray-light-600`}
      data-testid="epic-signin-own-login"
    >
      <span className="min-w-0">Using your own Epic login for this run.</span>
      <button
        type="button"
        onClick={() => {
          onUseSpLoginChange(true);
          onRecheckSpReadiness();
        }}
        disabled={submitting}
        data-testid="epic-signin-undo"
        className={`flex-shrink-0 rounded px-1.5 py-0.5 ${TYPE_BODY} text-primary-gray-light-700 underline hover:text-primary-gray-light-900`}
      >
        Undo
      </button>
    </div>
  ) : null;

  const leadingContent = (
    <>
      {renderClientSection && renderClientSection()}
      {clientGateBlocked && (
        <div
          className="rounded-md bg-primary-warning-50 border border-primary-warning-200 px-3 py-2"
          role="status"
          aria-live="polite"
        >
          <p className="text-xs font-semibold text-primary-warning-700">
            Select a client to continue.
          </p>
        </div>
      )}
      {titleRow(confirmTitle)}
      <PolicyPrefillSelector
        values={values}
        setValues={setValues}
        disabled={launchDisabled}
      />
      {submitError && (
        <div
          className="rounded-md bg-primary-error-50 border border-primary-error-200 px-3 py-2"
          role="alert"
        >
          <p className="text-xs font-semibold text-primary-error-700">
            {submitError}
          </p>
        </div>
      )}
    </>
  );

  const runLevelControls = shouldShowOversight(catalogEntry) ? (
    <OversightLevelSelector
      value={oversightLevel}
      onChange={onOversightLevelChange}
      disabled={submitting}
    />
  ) : null;

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-2" data-testid="prelaunch-body">
      {/* The workflow's catalog description is deliberately NOT rendered here
          (operator request 2026-07-28): it is engineer-facing prose about HITL
          and DT internals, and it pushed the actual form below the fold in the
          task pane. The description still rides the catalog entry for tooltips /
          other surfaces — only this pre-launch header drops it. */}

      {renderInputForm ? (
        renderInputForm({
          schema,
          values,
          setValues,
          onSubmit,
          submitting,
          fieldConfidence,
          fieldLoading,
          signInSection,
          submitDisabledReason: launchBlockReason,
          // The value the drawer already holds — it was a drawer/PreLaunchBody
          // prop and a `fieldLoading` source, but never reached the form.
          prefillLoading,
          leadingContent,
          runLevelControls,
        })
      ) : (
        <>
          {leadingContent}
          {signInSection}
          {runLevelControls}
          <SchemaFormFallback
            schema={schema}
            values={values}
            setValues={setValues}
            submitting={submitting}
            submitDisabledReason={launchBlockReason}
            onSubmit={onSubmit}
          />
        </>
      )}
    </div>
  );
}

/**
 * Concurrent-run confirmation.
 *
 * Shown when the substrate refused a launch because another non-terminal run
 * already exists for the same email. The guard exists to stop DUPLICATE PCHGs
 * and drafts, but "already running" is not the same as "must not run" — the
 * same email legitimately needs a second run when the first draft was wrong,
 * when the email names a second policy, or when a run's tail failed. Status
 * alone can't distinguish those, so this hands the call to the operator with
 * enough context to make it: which run, what state, how long ago.
 *
 * Deliberately NOT a plain window.confirm: the whole value is showing the state
 * of the in-flight run, which a native confirm can't render.
 */
function ConcurrentRunConfirm({
  instance,
  onCancel,
  onContinue,
}: {
  instance: ActiveWorkflow;
  onCancel: () => void;
  onContinue: () => void;
}) {
  const phase = stepDisplayName(instance.current_phase, null);
  const started = instance.created_at
    ? formatRelativeTime(instance.created_at)
    : null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-base-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="concurrent-run-title"
      data-testid="concurrent-run-confirm"
    >
      <div className="w-full max-w-sm rounded-lg bg-base-white p-4 shadow-lg">
        <h2
          id="concurrent-run-title"
          className="text-sm font-semibold text-primary-gray-light-900"
        >
          A workflow is already running for this email
        </h2>

        <div className="mt-2 rounded-md border border-primary-gray-light-200 bg-primary-gray-light-50 px-3 py-2">
          <p
            className="text-xs text-primary-gray-light-700"
            data-testid="concurrent-run-status"
          >
            Status:{" "}
            <span className="font-semibold">
              {statusDisplayLabel(instance.status)}
            </span>
            {phase && <> · {phase}</>}
          </p>
          {started && (
            <p className="mt-0.5 text-xs text-primary-gray-light-600">
              Started {started}
            </p>
          )}
        </div>

        <p className="mt-2 text-xs text-primary-gray-light-600">
          Running it again will create a second policy change and a second
          draft reply. Continue only if you meant to run this email twice.
        </p>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            data-testid="concurrent-run-cancel"
            className="rounded-md border border-primary-gray-light-300 px-3 py-1.5 text-xs font-semibold text-primary-gray-light-700 hover:bg-primary-gray-light-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onContinue}
            data-testid="concurrent-run-continue"
            className="rounded-md bg-sunrise px-3 py-1.5 text-xs font-semibold text-base-white hover:opacity-90"
          >
            Continue anyway
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Minimal JSON-textarea fallback used until Agent B's SchemaForm lands.
 * Honors plan §"SchemaForm" guidance: when schema is unsupported,
 * render a JSON textarea with a warning banner. This is the fallback
 * branch by default since we don't have the real schema renderer yet.
 */
function SchemaFormFallback({
  schema,
  values,
  setValues,
  submitting,
  submitDisabledReason,
  onSubmit,
}: {
  schema: Record<string, unknown> | null;
  values: Record<string, unknown>;
  setValues: (v: Record<string, unknown>) => void;
  submitting: boolean;
  /** Same contract as JsonSchemaForm's prop — see the render-prop type above. */
  submitDisabledReason?: string | null;
  onSubmit: (next: Record<string, unknown>) => void;
}) {
  const [text, setText] = useState<string>(() =>
    JSON.stringify(values, null, 2),
  );
  const [parseError, setParseError] = useState<string | null>(null);

  const handleSubmit = () => {
    let parsed: Record<string, unknown> = {};
    if (text.trim().length) {
      try {
        parsed = JSON.parse(text);
      } catch (err) {
        setParseError(`Invalid JSON: ${String(err)}`);
        return;
      }
    }
    setParseError(null);
    setValues(parsed);
    onSubmit(parsed);
  };

  const launchButton = (
    <button
      type="button"
      disabled={submitting || !!submitDisabledReason}
      onClick={handleSubmit}
      className="w-full px-3 py-2 rounded-md bg-sunrise text-primary-gray-light-900 text-sm font-semibold hover:bg-blossom transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
      data-testid="prelaunch-launch"
    >
      {submitting ? (
        <span className="flex items-center gap-1.5">
          <span className="h-3.5 w-3.5 animate-spin rounded-full border border-primary-gray-light-700/30 border-t-primary-gray-light-900 flex-shrink-0" />
          Launching…
        </span>
      ) : "Launch"}
    </button>
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-md bg-primary-warning-50 border border-primary-warning-200 px-3 py-2">
        <p className="text-2xs text-primary-warning-700">
          Generic input editor (Agent B's SchemaForm not yet wired). Edit the
          JSON inputs directly.
          {schema ? " Schema is shown for reference below." : ""}
        </p>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={8}
        className="w-full font-mono text-xs rounded-md border border-primary-gray-light-300 px-3 py-2"
        spellCheck={false}
        data-testid="prelaunch-json"
      />
      {schema && (
        <details className="text-2xs text-primary-gray-light-500">
          <summary>Input schema</summary>
          <pre className="overflow-auto bg-primary-gray-light-25 p-2 rounded mt-1">
            {JSON.stringify(schema, null, 2)}
          </pre>
        </details>
      )}
      {parseError && (
        <p className="text-2xs text-primary-error-700" role="alert">
          {parseError}
        </p>
      )}
      {/* Why it is greyed out, in ora's tooltip, not a native title (story
          28633). The span is the hover target: a disabled button fires no
          pointer events. Node title + describeChild: no aria-label or native
          title lands on it; a null title never opens. Always wrapped, so the
          button is not remounted when it enables. */}
      <CustomTooltip
        id="prelaunch-launch"
        title={submitDisabledReason ? <span>{submitDisabledReason}</span> : null}
        describeChild
        placement="top"
        arrow
        isOnModal
      >
        <span className="flex w-full">{launchButton}</span>
      </CustomTooltip>
    </div>
  );
}

// (Former local StepsSection helper removed — every call site now uses the
// shared `WorkflowStepsSection` from `@product-app/common/workflows/components/`.)

// ---------------------------------------------------------------------------
// live body
// ---------------------------------------------------------------------------

function LiveBody({
  push,
  warningAck,
  phases,
  phaseStatuses,
  instanceId,
  clientSk,
  fieldConfidence,
  clientName: clientNameProp,
  hitlQuestion,
  artifacts,
  artifactsLoading,
  onArtifactClick,
  fetchArtifactPayload,
  onEvidenceInteract,
  hitlEvents,
  hitlLoading,
  onHitlResponded,
  onClaimedAutoClose,
  phaseStream,
  stepView,
  stepViewName,
  requestSection,
  runSteps,
  debugActions,
  moreInfoOpen,
  onMoreInfoClose,
  openItemMessageId,
}: {
  push: InstancePushSnapshot;
  /**
   * Warning acknowledgement: the drawer's ONE live state (the same object the
   * header pill and TerminalBody read). Story 28633: a live run's lines render
   * here with their Acknowledge, not only once the run has finished.
   */
  warningAck: WarningAckState;
  phases: PhaseDefinition[];
  phaseStatuses: ReturnType<typeof derivePhaseStatusesFromInstance>;
  instanceId: string | null;
  /**
   * The message the pane is currently showing. Used ONLY to hide the "open the
   * original request" hop when the Servicer is already reading it — see
   * SourceRequestRow, which documents why that comparison is best-effort.
   */
  openItemMessageId?: string | null;
  clientSk: number | null;
  /** Per-field analyze_email confidence, forwarded to the shared DecisionCard. */
  fieldConfidence?: Record<string, "high" | "medium" | "low">;
  clientName?: string | null;
  hitlQuestion: HitlQuestion | null | undefined;
  artifacts: ArtifactDto[];
  artifactsLoading: boolean;
  onArtifactClick: (index: number) => void;
  fetchArtifactPayload: (
    instanceId: string,
    filename: string,
  ) => Promise<unknown>;
  /**
   * "The Servicer is working in the evidence card." Holds the running view's
   * auto-close — see `holdStepViewAutoClose` on the drawer above, which is the
   * only thing it ever does. Never an answer, never a submit.
   */
  onEvidenceInteract?: () => void;
  hitlEvents: HitlEvent[];
  hitlLoading: boolean;
  onHitlResponded: () => void;
  /**
   * Close the pane once the "I sent it" claim's countdown elapses. Wired to
   * the drawer's `onAutoClose ?? onClose` for the same reason the running
   * view's auto-close is: an automatic leave is not a manual one. Never an
   * answer — the claim was already submitted by the button.
   */
  onClaimedAutoClose?: () => void;
  /** running-drawer's phase / milestone main stream; null when the flag is off. */
  phaseStream?: React.ReactNode | null;
  /**
   * step-views' hero pane for this moment in the run (story 26739). Built ONCE
   * on the exported drawer from `push.status` and handed to BOTH inner trees;
   * null when `emuw-step-views` is off, which reproduces today's body exactly.
   */
  stepView?: React.ReactNode | null;
  /**
   * WHICH step view that node is, or null when the flag is off. Read here only
   * to decide what today's body must STOP drawing (the step counter, and the
   * standalone source-request row the exception view re-parents) - never to
   * re-derive the view, which happens once, above.
   */
  stepViewName?: string | null;
  /** running-drawer's rollback lever, resolved once on the exported drawer. */
  /** running-drawer's submitted-request section; null when the flag is off. */
  requestSection?: React.ReactNode | null;
  /** running-drawer's detailed chronological log; null when the flag is off. */
  /** Run inputs for the record's chronological view, built by the PARENT --
   *  it is the only scope holding the catalog. Each body completes the record
   *  with its own run values (started/ended/triggered-by). */
  runSteps: RunEvidenceStepsInput;
  /** Copy-id / kill-session links + SignalR test — More information pane only. */
  debugActions?: React.ReactNode | null;
  /** Whether the more-information pane is open. Owned by the top-level
   *  drawer component (survives step/phase updates); the entry-point button
   *  itself lives in the sticky footer, which the top-level drawer renders
   *  outside this body entirely. */
  moreInfoOpen?: boolean;
  /** Called when the pane's own back button is clicked. */
  onMoreInfoClose?: () => void;
}) {
  // Hooks must be called before any early return (React rules of hooks)
  const effectiveStatus = push.status?.status ?? null;
  const { family } = useDebouncedFamily(effectiveStatus, hitlQuestion, instanceId);
  const persistKeyFor = (section: string) =>
    `outlook-drawer:${family}:${section}`;
  const { details: clientCtxDetails, loading: clientCtxLoading } =
    useClientDetails(clientSk);

  // A pending_email_send instance has already produced its full result. Fetch
  // it (same endpoint TerminalBody uses) so the hand-off drawer can show the
  // Result accordion just like a truly-completed run. Only fetches once the
  // instance is parked — running instances have no final result yet.
  const [pesResult, setPesResult] = useState<WorkflowResultBody | null>(null);
  const pesFetchedFor = useRef<string | null>(null);
  useEffect(() => {
    if (effectiveStatus !== "pending_email_send" || !instanceId) return;
    if (pesFetchedFor.current === instanceId) return;
    pesFetchedFor.current = instanceId;
    void orchestratorApi
      .getWorkflowResult(instanceId)
      .then((r) => {
        const result = r.result;
        setPesResult(
          result && typeof result === "object" && Object.keys(result).length > 0
            ? (result as WorkflowResultBody)
            : null,
        );
      })
      .catch(() => setPesResult(null));
  }, [effectiveStatus, instanceId]);

  // HITL submit latch — hosted HERE (the stable LiveBody) rather than inside
  // ReviewCheckpointPanel so it survives that panel remounting when the post-
  // submit `authoritative` burst poll briefly drops hitl_question. Without it
  // the Approve pill re-enabled on the still-pending question and the AM had to
  // click twice (client/policy verification gates). See useHitlSubmitLatch.
  // shared-decision-components: CLASSIFY the ack rather than discarding it.
  //
  // Two signals, both previously thrown away by this surface:
  //   * a 200 whose body says `already_handled` — a warm gate somebody else
  //     answered, which used to look exactly like success;
  //   * an ApiError carrying HTTP 409 — a cold gate somebody else answered, which
  //     used to surface as a generic retryable error.
  //
  // Both classify to `refused`, which the latch treats as PERMANENT: the controls
  // stay held and nothing further is submitted for that decision.
  const resumeCurrentInstance = useCallback(
    async (
      payload: Record<string, unknown>,
    ): Promise<DecisionAnswerOutcome | void> => {
      if (!instanceId) return;
      try {
        const ack = await orchestratorApi.resumeInstance(instanceId, payload);
        return classifyResumeAck(ack);
      } catch (err) {
        // `status` is null for a transport failure (offline, abort), which the
        // classifier correctly treats as a retryable error rather than a refusal.
        const status = err instanceof ApiError ? err.status : null;
        const message =
          err instanceof Error ? err.message : "Could not submit your answer.";
        return classifyResumeFailure(status, message);
      }
    },
    [instanceId],
  );
  const {
    isSubmitting: isHitlSubmitting,
    error: hitlError,
    refusal: hitlRefusal,
    submit: handleHitlSubmit,
  } = useHitlSubmitLatch({
    hitlQuestion,
    onResume: resumeCurrentInstance,
    onResponded: onHitlResponded,
  });

  // shared-decision-components AC-3: the decision cleared and THIS surface did
  // not answer it. Derived by the shared hook so both hosts derive it identically
  // — a host that computed "the question went away" itself is the per-host variant
  // AC-1 forbids.
  const { answeredElsewhere } = useAnsweredElsewhere({
    hitlQuestion,
    instanceId: instanceId ?? null,
    submittedHere: isHitlSubmitting || hitlRefusal != null,
  });
  const decisionAnswered = hitlRefusal != null || answeredElsewhere;

  // A refusal takes precedence over the error slot: it is not an error, and
  // showing a retry-flavoured message over an already-answered decision invites
  // exactly the retry that cannot work. Wording from the SHARED copy module.
  const hitlNotice = hitlRefusal ? decisionRefusedBody(null) : hitlError;

  // The card resolves the recorded answer itself; the host supplies only the
  // transport. Must REFETCH — this pane's cached `hitlEvents` were fetched before
  // the answer landed and its cache key does not change on this transition.
  const fetchHitlHistoryForCard = useCallback(
    async (id: string) => {
      const res = await orchestratorApi.getHitlHistory(id);
      return res?.events ?? [];
    },
    [],
  );

  // Gate-declared side actions (x-actions) — e.g. "Check Now" on pending_edocs.
  // Lifted verbatim out of the deleted ReviewCheckpointPanel: it runs the
  // per-instance eDocs poll and does NOT resume the workflow.
  const handleCardSideAction = useCallback(
    async (_endpoint: string): Promise<SideActionResult> => {
      if (!instanceId) return { ok: false, error: "No instance." };
      try {
        const { outcome } = await orchestratorApi.checkEdocs(instanceId);
        return { ok: true, message: edocCheckMessage(outcome) };
      } catch (err) {
        return {
          ok: false,
          error:
            err instanceof Error ? err.message : "Check failed. Please try again.",
        };
      }
    },
    [instanceId],
  );

  // running-drawer: the carrier-evidence decision card. Gated on the DECISION
  // being present, never on `status === "pending_edocs"` — a run carrying the
  // decision before it reaches the evidence step is `running` and dispatches to
  // ActiveDrawerBody, and gating on the parked status makes that path
  // unreachable (AC-6).
  //
  // It is built HERE, in LiveBody, and deliberately NOT in TerminalBody: there
  // is no branch left to choose on a finished run (Phase 3 deliverable 4). That
  // is the opposite of `phaseStream`, which BOTH inner trees need — hence the
  // stream is built once on the exported drawer and this is not.
  //
  // ONE answer transport: `onSubmitBranch` calls the SAME `handleHitlSubmit`
  // that reject, take-over and evidence already use, which is
  // `orchestratorApi.resumeInstance` → `POST …/resume` → `resume_workflow`.
  // No new client method (design Constraint 17). The latch is the existing
  // `useHitlSubmitLatch` above, hosted on this stable parent — never inside the
  // card, where a post-submit remount would lose it.
  //
  // The candidate itself — recorded decision, else the v2 wait placeholder —
  // comes from `carrierEvidenceCandidateFor` in the shared phase model, which
  // is the ONE copy of that rule for both hosts (review finding #28).
  const evidenceCandidate = carrierEvidenceCandidateFor(push.status);
  const evidenceDecision =
    shouldShowCarrierEvidenceCard(
      push.status?.status,
      liveCarrierEvidenceStepId(push.status),
      evidenceCandidate,
    )
      ? evidenceCandidate
      : null;
  // ONE component for the whole carrier-evidence surface. The panel owns the
  // presentation copy, the branch controls, the single uploader and its own
  // per-control busy state — this host supplies transport and nothing else.
  //
  // The `evidenceBusy` flag that used to live here is GONE. It fed both the
  // card's button and the uploader's, so one click blanked the other, and it
  // was cleared only when the snapshot's branch flipped to `am_supplies` —
  // which never happens on the placeholder-card path, leaving the region
  // disabled and wordless.
  const decisionCard = evidenceDecision ? (
    <CarrierEvidencePanel
      model={evidenceDecision}
      onSubmitBranch={(action) => handleHitlSubmit({ decision: action })}
      onSubmitEvidence={(payload) => handleHitlSubmit(payload)}
      parked={(push.status?.status ?? "").toLowerCase() === "pending_edocs"}
      onInteract={onEvidenceInteract}
      submitError={hitlError}
      instanceId={instanceId}
      workflowId={push.status?.workflow_id ?? null}
    />
  ) : null;

  // email-send-complete-override, Phase 3 task 6: the parked-run clear rides
  // the SAME resumeInstance transport (AD-P2) — no new client method. A
  // dedicated latch instance (hitlQuestion: null — pending_email_send never
  // carries one) keeps its pending/error state independent of the HITL
  // question latch above rather than overloading hitlError for an unrelated
  // action.
  const {
    isSubmitting: isClearSubmitting,
    error: clearSubmitError,
    submit: handleClearSubmit,
  } = useHitlSubmitLatch({
    hitlQuestion: null,
    onResume: resumeCurrentInstance,
    onResponded: onHitlResponded,
  });

  // "I sent it" claim (Plan 2, story 26752 item 10): rides the SAME
  // resumeInstance transport as the clear path above (design Constraint
  // 17 — the substrate's decision-token peek diverts `{claim: "reply_sent"}`
  // to `reply_claim_sent.py` exactly as it diverts the clear disposition to
  // `pending_send_clear.py`; no new client method, no new route). A SECOND
  // dedicated latch instance (not the clear latch, not the HITL latch above)
  // so a claim error never shows in the clear error slot and vice versa —
  // same reasoning as the clear latch's own comment: independent
  // pending/error state for an unrelated action sharing one transport.
  const {
    isSubmitting: isClaimSubmitting,
    error: claimSubmitError,
    submit: handleClaimSubmit,
  } = useHitlSubmitLatch({
    hitlQuestion: null,
    onResume: resumeCurrentInstance,
    onResponded: onHitlResponded,
  });

  if (!push.status && push.loading) {
    return (
      <div className="flex flex-col gap-3" data-testid="live-loading">
        <Skeleton width={120} height={20} />
        <Skeleton width="100%" height={120} />
      </div>
    );
  }

  // Context fields from workflow inputs + metadata.client_context fallback.
  // The substrate frequently echoes inputs.* on launch but clears them on
  // later push frames; metadata.client_context is preserved across pushes
  // and is the same key the plugin's TerminalBody reads.
  const inputs = (push.status?.inputs ?? {}) as Record<string, unknown>;
  const inputClientName = inputs.client_name as string | undefined;
  const inputPolicyNumber = inputs.policy_number as string | undefined;
  const liveMetaCtx = (push.status?.metadata as Record<string, unknown> | undefined)
    ?.client_context as Record<string, unknown> | undefined;
  // A prospect run (ora's client Workflows drawer) has no client: show the
  // prospect and its opportunity instead, as ora's mapInstanceToRow does.
  const metaPolicyNumber = ((liveMetaCtx?.policy as Record<string, unknown> | undefined)?.number ??
    (liveMetaCtx?.opportunity as Record<string, unknown> | undefined)?.name) as string | undefined;
  const liveMetaClientName = ((liveMetaCtx?.client as Record<string, unknown> | undefined)?.name ??
    (liveMetaCtx?.prospect as Record<string, unknown> | undefined)?.name) as string | undefined;
  const triggeredBy = (push.status?.metadata as Record<string, unknown> | undefined)
    ?.triggered_by as string | undefined;

  // Run section — the full field set, matching ora. The plugin previously
  // rendered only RunChip (Started / Triggered by / Version, with `endedAt`
  // hardcoded null so Duration never appeared) and had no `sections.run` at
  // all, so it was strictly poorer than ora's. Both are RunSection now.
  const runStartedAt = push.status?.created_at ?? null;
  const runSection = (
    <RunSection
      triggeredByName={triggeredBy ?? null}
      startedAtFormatted={runStartedAt ? formatAbsolute(runStartedAt) : null}
      startedAtShort={runStartedAt ? formatTime(runStartedAt) : null}
      // A live run has not ended; duration counts up from the start
      // (computeDurationFromDates treats a null end as "now").
      endedAtFormatted={null}
      duration={runStartedAt ? computeDurationFromDates(runStartedAt, null) : null}
      currentStep={
        push.status?.current_phase ? titleCase(push.status.current_phase) : null
      }
      // step-views: the counter is the thing this surface exists to
      // replace. With a step view on it is suppressed here and survives
      // only inside "All phases" (design decision D4).
      progress={
        stepViewName == null &&
        push.status?.step_index != null &&
        push.status?.total_steps != null
          ? `Step ${push.status.step_index + 1} of ${push.status.total_steps}`
          : null
      }
      workflowVersion={displayWorkflowVersion(
        push.status?.workflow_id ?? null,
        push.status?.major_version as number | null | undefined,
      )}
      // Collapsed while the run is live, expanded once terminal — the same
      // `defaultExpanded={isTerminal}` split ora uses. Explicit because
      // DrawerSection's own default is `true`.
      defaultExpanded={false}
      persistKey={persistKeyFor("run")}
    />
  );

  // The run record shown behind "More information" — the SAME record ora's
  // drawer and /workflows/runs/<id> render. Built from exactly the values
  // RunSection above already uses, so the record's Overview cannot disagree
  // with the section beside it.
  const recordClientContext = readClientContext(
    (push.status?.metadata ?? null) as Record<string, unknown> | null,
  );
  // CONTENT ONLY, not a section: the run record owns the Result accordion and
  // its leading sentence. A DrawerSection here is what rendered Result twice.
  const pendingResultBlock =
    effectiveStatus === "pending_email_send" ? (
      <WorkflowResultSection result={pesResult} jsonFallback />
    ) : null;

  const record: RunRecordBodyProps = {
    workflowTitle: titleCaseId(
      push.status?.workflow_id ?? null,
    ),
    status: (push.status?.status ?? "running") as WorkflowStatus,
    // The record's pill: the Servicer's OWN documents are Needs You (28633).
    waitSignals: { hitlQuestion: push.status?.hitl_question },
    clientName: recordClientContext.clientName,
    policyNumber: recordClientContext.policyNumber,
    triggeredByName: triggeredBy ?? null,
    startedAtFormatted: runStartedAt ? formatAbsolute(runStartedAt) : null,
    endedAtFormatted: null,
    duration: runStartedAt ? computeDurationFromDates(runStartedAt, null) : null,
    currentStep: push.status?.current_phase
      ? titleCase(push.status.current_phase)
      : null,
    progress:
      push.status?.step_index != null && push.status?.total_steps != null
        ? `Step ${push.status.step_index + 1} of ${push.status.total_steps}`
        : null,
    workflowVersion: displayWorkflowVersion(
      push.status?.workflow_id ?? null,
      push.status?.major_version as number | null | undefined,
    ),
    artifacts,
    hitlEvents,
    // The RUN, not a derived chronology — RunStepsLog owns the manifest
    // resolution. Assembling these inputs per host is the exact bug recorded
    // above: 17 never-run steps in ora, 45 all-green rows here.
    runSteps,
    // The add-in does not call get_run_decisions; decisions derive from HITL
    // history alone, which is what this surface already showed.
    decisionsPayload: null,
    decisionsDegraded: true,
    resultBody: null,
    resultDetail: pendingResultBlock,
    // Story 28633: the record's Result lists the same lines with their
    // Acknowledge, as ora's drawer record already does on a live run.
    warningAcknowledgements: warningAck,
  };

  // Story 28633: the run's warning lines with their Acknowledge, while the run
  // is live — the SAME shared list TerminalBody renders once it has finished.
  // Every live body takes it in its `warningLines` slot; no lines, no list (and
  // the pending-email-send body keeps its old banners).
  const warningLines =
    (warningAck.items?.length ?? 0) > 0 ? <WarningAcknowledgementList ack={warningAck} /> : null;

  // Sections map for the shared body components
  const sections: DrawerSectionMap = {
    // running-drawer: the ONE new section key (design Constraint 20).
    request: requestSection ?? null,
    input_needed:
      (hitlQuestion || decisionAnswered) && instanceId ? (
        <DecisionCard
          hitlQuestion={(hitlQuestion ?? null) as Record<string, unknown> | null}
          onSubmit={handleHitlSubmit}
          isSubmitting={isHitlSubmitting}
          error={hitlNotice}
          refusal={hitlRefusal}
          answeredElsewhere={answeredElsewhere}
          instanceId={instanceId}
          fetchHitlHistory={fetchHitlHistoryForCard}
          host="outlook-addin"
          fieldConfidence={fieldConfidence}
          onSideAction={handleCardSideAction}
        />
      ) : null,
  };

  // PinnedHeader data — multi-source fallback chain (uses `||` so empty
  // strings fall through too):
  //   1. workflow inputs.{field} (substrate-echoed)
  //   2. live metadata.context.{client|policy}
  //   3. drawer's clientNameProp / fetched client details
  const displayClientName =
    inputClientName ||
    liveMetaClientName ||
    clientNameProp ||
    clientCtxDetails?.client.client_name ||
    null;
  const displayPolicyNumber = inputPolicyNumber || metaPolicyNumber || null;

  // Result accordion for the pending_email_send hand-off — same shape as
  // TerminalBody's completed result. Null for other live statuses (running has
  // no final result).

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col" data-testid="live-body">
      <div className="flex-1 min-h-0 overflow-y-auto">
      <PinnedHeader
        clientName={displayClientName}
        policyNumber={displayPolicyNumber}
        rightLabel={null}
        clientDetails={clientCtxDetails}
        clientLoading={clientCtxLoading}
        // Story 26738. Landing here from an ora alert means the Servicer is
        // looking at ora's notification, not at the lender email that started
        // the run — and that email is usually what they need to read before
        // they can answer. The run has carried its `graph_message_id` since
        // launch, so the hop back is one click, in the header on EVERY view
        // (the exception view used to re-parent it into "Launched from").
        // Renders nothing when the run has no source email, which is every
        // hand-launched run.
        action={
          <SourceRequestRow
            sourceMessageId={
              typeof push.status?.metadata?.graph_message_id === "string"
                ? push.status.metadata.graph_message_id
                : null
            }
            currentMessageId={openItemMessageId}
          />
        }
      />

      <div className="p-1 pt-3 flex flex-col">
        {family === "hitl" ? (
          <HitlDrawerBody
            sections={sections}
            phaseStream={phaseStream}
            stepView={stepView}
            record={record}
            debugActions={debugActions}
                  moreInfoOpen={moreInfoOpen}
                  onMoreInfoClose={onMoreInfoClose}
            decisionCard={decisionCard}
            warningLines={warningLines}
          />
        ) : family === "pending_email_send" ? (
          <PendingEmailSendDrawerBody
            sections={sections}
            resultWarnings={(() => {
              const fromFetch = extractResultWarnings(
                pesResult as Record<string, unknown> | null,
              );
              return fromFetch.length > 0
                ? fromFetch
                : warningsFromStatus(push.status);
            })()}
            phaseStream={phaseStream}
            stepView={stepView}
            record={record}
            debugActions={debugActions}
                  moreInfoOpen={moreInfoOpen}
                  onMoreInfoClose={onMoreInfoClose}
            decisionCard={decisionCard}
            warningLines={warningLines}
            onClear={(payload) => {
              void handleClearSubmit(payload);
            }}
            clearPending={isClearSubmitting}
            clearError={clearSubmitError}
            onClaimSent={() => {
              void handleClaimSubmit({ claim: "reply_sent" });
            }}
            claimSentPending={isClaimSubmitting}
            claimSentError={claimSubmitError}
            claimedSentAt={push.status?.reply_claimed_sent_at ?? null}
            onClaimedAutoClose={onClaimedAutoClose}
            sweepCard={<SentReplySweepAction status={effectiveStatus} />}
          />
        ) : family === "pending_edocs" ? (
          <PendingEdocsDrawerBody
            sections={sections}
            hitlQuestion={(hitlQuestion ?? null) as Record<string, unknown> | null}
            onSideAction={async () => {
              if (!instanceId) return { ok: false, error: "No active instance." };
              try {
                const { outcome } = await orchestratorApi.checkEdocs(instanceId);
                return { ok: true, message: edocCheckMessage(outcome) };
              } catch (err) {
                return {
                  ok: false,
                  error:
                    err instanceof Error
                      ? err.message
                      : "Check failed. Please try again.",
                };
              }
            }}
            onReject={() => {
              void handleHitlSubmit({ decision: "reject" });
            }}
            rejectPending={isHitlSubmitting}
            rejectError={hitlError}
            // Identical wiring to ora's: the same shared body, the same
            // resume transport reject already uses, the same payloads. The
            // add-in adds no logic the drawer does not have (Constraint 17,
            // Constraint 20).
            onEvidenceSourceChange={(action) => {
              void handleHitlSubmit({ decision: action });
            }}
            evidencePending={isHitlSubmitting}
            phaseStream={phaseStream}
            stepView={stepView}
            record={record}
            debugActions={debugActions}
                  moreInfoOpen={moreInfoOpen}
                  onMoreInfoClose={onMoreInfoClose}
            decisionCard={decisionCard}
            warningLines={warningLines}
          />
        ) : (
          <ActiveDrawerBody
            sections={sections}
            stepIndex={push.status?.step_index ?? null}
            totalSteps={push.status?.total_steps ?? null}
            phaseStream={phaseStream}
            stepView={stepView}
            record={record}
            debugActions={debugActions}
                  moreInfoOpen={moreInfoOpen}
                  onMoreInfoClose={onMoreInfoClose}
            decisionCard={decisionCard}
            warningLines={warningLines}
          />
        )}

        {push.isStale && (
          <p className="text-2xs text-primary-warning-700">
            Status hasn't refreshed in a while — last update may be stale.
          </p>
        )}
      </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live client context card
// ---------------------------------------------------------------------------

/**
 * Thin fetch wrapper around the shared ClientContextPanel.
 * Owns the orchestratorApi transport; the visual is fully delegated to the
 * shared component so both ora and the plugin render identically.
 */
/**
 * useClientDetails — fetches ClientDetails for a given clientSk via the
 * plugin's orchestratorApi transport. The drawer's PinnedHeader consumes
 * the resulting { details, loading } to render the merged client-context
 * dropdown. Replaces the standalone <LiveClientContext> render.
 */
function useClientDetails(clientSk: number | null): {
  details: ClientDetails | null;
  loading: boolean;
} {
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<ClientDetails | null>(null);
  const fetchedFor = useRef<number | null>(null);

  useEffect(() => {
    if (!clientSk || fetchedFor.current === clientSk) return;
    fetchedFor.current = clientSk;
    setDetails(null); // clear stale data immediately so PinnedHeader shows skeleton
    setLoading(true);
    void orchestratorApi
      .getClientDetails(clientSk)
      .then(setDetails)
      .catch(() => setDetails(null))
      .finally(() => setLoading(false));
  }, [clientSk]);

  return { details, loading };
}

// ---------------------------------------------------------------------------
// terminal body
// ---------------------------------------------------------------------------

function TerminalBody({
  push,
  warningAck,
  doneOwnsWarningLines,
  pillNeedsAck,
  pillHasWarnings,
  resultData,
  phases,
  phaseStatuses,
  artifacts,
  artifactsLoading,
  onArtifactClick,
  fetchArtifactPayload,
  hitlEvents,
  hitlLoading,
  clientSk,
  clientName: clientNameProp,
  isEndorsement,
  failed,
  cancelled,
  phaseStream,
  stepView,
  stepViewName,
  requestSection,
  runSteps,
  debugActions,
  moreInfoOpen,
  onMoreInfoClose,
  openItemMessageId,
}: {
  push: InstancePushSnapshot;
  phases: PhaseDefinition[];
  phaseStatuses: ReturnType<typeof derivePhaseStatusesFromInstance>;
  artifacts: ArtifactDto[];
  artifactsLoading: boolean;
  onArtifactClick: (index: number) => void;
  fetchArtifactPayload: (
    instanceId: string,
    filename: string,
  ) => Promise<unknown>;
  hitlEvents: HitlEvent[];
  hitlLoading: boolean;
  instanceId: string | null;
  clientSk: number | null;
  clientName?: string | null;
  /** The Outlook item on screen — hides Open Email when it IS the source. */
  openItemMessageId?: string | null;
  isEndorsement: boolean;
  failed: boolean;
  cancelled: boolean;
  /** running-drawer's phase / milestone main stream; null when the flag is off. */
  phaseStream?: React.ReactNode | null;
  /**
   * step-views' hero pane (story 26739). Built once on the exported drawer and
   * handed to BOTH inner trees - a live-only wire is the dual-tree defect this
   * pane is prone to. Null when the flag is off.
   */
  stepView?: React.ReactNode | null;
  /** WHICH step view that node is, or null when the flag is off. */
  stepViewName?: string | null;
  /** running-drawer's submitted-request section; null when the flag is off. */
  requestSection?: React.ReactNode | null;
  /** running-drawer's rollback lever, resolved once on the exported drawer. */
  /** running-drawer's detailed chronological log; null when the flag is off. */
  /** Run inputs for the record's chronological view, built by the PARENT --
   *  it is the only scope holding the catalog. Each body completes the record
   *  with its own run values (started/ended/triggered-by). */
  runSteps: RunEvidenceStepsInput;
  /** Copy-id / kill-session links + SignalR test — More information pane only. */
  debugActions?: React.ReactNode | null;
  /** Whether the more-information pane is open. Owned by the top-level
   *  drawer component (survives step/phase updates); the entry-point button
   *  itself lives in the sticky footer, which the top-level drawer renders
   *  outside this body entirely. */
  moreInfoOpen?: boolean;
  /** Called when the pane's own back button is clicked. */
  onMoreInfoClose?: () => void;
  /** Warning acknowledgement (2026-10-06): the drawer's ONE live state. */
  warningAck: WarningAckState;
  /** The Done step view is showing the warning lines itself; don't repeat them. */
  doneOwnsWarningLines?: boolean;
  /** R89/R65: the SAME pill value the header uses (record badge reads it too). */
  pillNeedsAck: boolean | null;
  /** The SAME legacy-star value the header uses. */
  pillHasWarnings: boolean;
  /** GET /result body, fetched by the parent so the pill can see it. */
  resultData: WorkflowResultBody | null;
}) {
  const inst = push.status;
  // Warning acknowledgement: when the API publishes lines, ONE list (with an
  // Acknowledge per line) replaces the Ended-early banner, the metadata.warning
  // box and the *_warning boxes (R57/R59: metadata.warning is the
  // `metadata_warning` line). No lines, or an older API: every old banner renders
  // as before -- an empty list never hides a legacy warning.
  const ackList =
    (warningAck.items?.length ?? 0) > 0 && !doneOwnsWarningLines ? (
      <WarningAcknowledgementList ack={warningAck} />
    ) : null;
  const hasAckLine = (id: string) => (warningAck.items ?? []).some((i) => i.id === id);
  // I1/R89: the SAME stand-down booleans the parent's pill uses.
  const legacyShown = legacyWarningsShown(push.status, warningAck.items, resultData as Record<string, unknown> | null);
  const errorMessage = inst?.error_message ?? inst?.error ?? null;
  const terminalStatus = (inst?.status ?? "completed").toLowerCase();
  const family = pickDrawerStatusFamily(terminalStatus);
  const persistKeyFor = (section: string) =>
    `outlook-drawer:${family}:${section}`;

  // Hook: client details for the merged PinnedHeader. Must run before any
  // conditional early-return below to satisfy rules-of-hooks.
  const { details: clientCtxDetails, loading: clientCtxLoading } =
    useClientDetails(clientSk);

  // Carrier-handoff payload — surfaces `carrier_handoff` for endorsement
  // workflows.
  //
  // Reads the fetched RESULT BODY. It used to read `inst.outputs`, a status key
  // nothing has ever emitted, so this tile could never render — the same dead
  // source that kept `FinalStateCard` (whose `excludeKeys` deferred
  // carrier_handoff to this tile) off the screen for its whole life.
  //
  // ⚠️ Pointing it at the result body makes it POSSIBLE, not guaranteed: no
  // orchestration in the workflows repo currently emits a `carrier_handoff`
  // object in its result either (it exists there only as a resume `pause_class`
  // string). So this tile is still expected to be absent until something
  // publishes that field. Left in place rather than deleted because that is a
  // product decision, not a cleanup — see the dead-surface note in the plan.
  const carrierHandoffData = useMemo(() => {
    const out = resultData ?? {};
    const handoff = (out as { carrier_handoff?: unknown }).carrier_handoff;
    if (!handoff || typeof handoff !== "object") return null;
    const h = handoff as Record<string, unknown>;
    return {
      url: typeof h.url === "string" ? h.url : null,
      instructions: typeof h.instructions === "string" ? h.instructions : null,
      carrierName:
        typeof h.carrier_name === "string"
          ? (h.carrier_name as string)
          : typeof h.carrierName === "string"
            ? (h.carrierName as string)
            : null,
      hasCarrierConnect: Boolean(h.has_carrier_connect ?? h.hasCarrierConnect),
    };
  }, [resultData]);

  const triggeredByName =
    (inst?.metadata as Record<string, unknown> | undefined)?.triggered_by as
      | string
      | undefined;
  const startedAt = inst?.created_at ?? null;
  const endedAt = inst?.updated_at ?? null;

  // Duration label for PinnedHeader
  const durationLabel = startedAt
    ? computeDurationFromDates(startedAt, endedAt)
    : null;

  // Context fields from inputs for PinnedHeader
  const inputs = (inst?.inputs ?? {}) as Record<string, unknown>;
  const inputClientName = inputs.client_name as string | undefined;
  const inputPolicyNumber = inputs.policy_number as string | undefined;
  const metaCtx = (inst?.metadata as Record<string, unknown> | undefined)
    ?.client_context as Record<string, unknown> | undefined;
  // Prospect fallback, as in LiveBody above and ora's mapInstanceToRow.
  const metaClientName = ((metaCtx?.client as Record<string, unknown> | undefined)?.name ??
    (metaCtx?.prospect as Record<string, unknown> | undefined)?.name) as string | undefined;
  const metaPolicyNumber = ((metaCtx?.policy as Record<string, unknown> | undefined)?.number ??
    (metaCtx?.opportunity as Record<string, unknown> | undefined)?.name) as string | undefined;

  // Run section — same component and field set as ora and as LiveBody. A
  // cancelled run has no meaningful end time (the row is stamped when the
  // cancel lands, not when work stopped), so Ended/Duration stay empty for it
  // — the behaviour the old chip encoded as `endedAt: cancelled ? null : …`.
  const runEndedAt = cancelled ? null : endedAt;
  const runSection = (
    <RunSection
      triggeredByName={triggeredByName ?? null}
      startedAtFormatted={startedAt ? formatAbsolute(startedAt) : null}
      startedAtShort={startedAt ? formatTime(startedAt) : null}
      endedAtFormatted={runEndedAt ? formatAbsolute(runEndedAt) : null}
      duration={
        startedAt && runEndedAt
          ? computeDurationFromDates(startedAt, runEndedAt)
          : null
      }
      // A terminal run has no current step.
      currentStep={null}
      // step-views: same suppression as the live body (design decision D4).
      progress={
        stepViewName == null &&
        inst?.step_index != null &&
        inst?.total_steps != null
          ? `Step ${inst.step_index + 1} of ${inst.total_steps}`
          : null
      }
      workflowVersion={displayWorkflowVersion(
        inst?.workflow_id ?? null,
        inst?.major_version as number | null | undefined,
      )}
      defaultExpanded
      persistKey={persistKeyFor("run")}
    />
  );

  // The run record behind "More information" — see the LiveBody note.
  const recordClientContext = readClientContext(
    (inst?.metadata ?? null) as Record<string, unknown> | null,
  );
  const resultBlock =
    (terminalStatus === "completed" ||
      terminalStatus === "failed" ||
      terminalStatus === "rejected" ||
      terminalStatus === "timeout_cancelled")
      // Content only — see the live tree's note.
      ? <WorkflowResultSection result={resultData} jsonFallback />
      : null;

  const record: RunRecordBodyProps = {
    workflowTitle: titleCaseId(inst?.workflow_id ?? null),
    status: (inst?.status ?? "completed") as WorkflowStatus,
    clientName: recordClientContext.clientName,
    policyNumber: recordClientContext.policyNumber,
    triggeredByName: triggeredByName ?? null,
    startedAtFormatted: startedAt ? formatAbsolute(startedAt) : null,
    endedAtFormatted: runEndedAt ? formatAbsolute(runEndedAt) : null,
    duration:
      startedAt && runEndedAt
        ? computeDurationFromDates(startedAt, runEndedAt)
        : null,
    currentStep: null,
    progress:
      inst?.step_index != null && inst?.total_steps != null
        ? `Step ${inst.step_index + 1} of ${inst.total_steps}`
        : null,
    workflowVersion: displayWorkflowVersion(
      inst?.workflow_id ?? null,
      inst?.major_version as number | null | undefined,
    ),
    artifacts,
    artifactsLoading,
    hitlEvents,
    runSteps,
    // See ora's drawer: the pane does not render the milestone summary.
    phaseManifest: null,
    decisionsPayload: null,
    decisionsDegraded: true,
    resultBody: null,
    resultDetail: resultBlock,
    requestBlock: requestSection ?? null,
    needsAcknowledgement: pillNeedsAck,
    warningAcknowledgements: warningAck,
    // An API that predates acknowledgement: the overview badge keeps the legacy "*".
    hasWarnings: pillHasWarnings,
  };

  // Sections map for shared body components
  const sections: DrawerSectionMap = {
    // running-drawer: the ONE new section key (design Constraint 20).
    request: requestSection ?? null,
  };

  // Result block for completed workflows

  // Non-fatal post-completion warning — parity with ora's WorkflowDetailDrawer.
  // Covers every completed-run caveat the orchestration emits (verify-unconfirmed,
  // Epic write rejections, verify mismatches) via a plain warning string in
  // metadata — no count guard, since a write-failure warning can carry
  // verify_unconfirmed_count === 0. Surface it so a completed run is visibly
  // distinct from a clean one. Tailwind (not MUI) to match this app's own alert
  // blocks.
  const verificationWarningBlock = (() => {
    if (terminalStatus !== "completed") return null;
    // R57/R59/R89 (2026-10-06): a legacy text stands down ONLY when the line
    // list carries its own line (`metadata_warning`; any `*_warning` result
    // line). A list without it keeps the text (a false warning beats a missing one).
    const md = (inst?.metadata as Record<string, unknown> | undefined) ?? {};
    const verifyWarning =
      legacyShown.metadata && typeof md.warning === "string" ? md.warning : null;
    const resultLinesShown = (warningAck.items ?? []).some((i) => i.kind === "result_warning");
    // Always-visible *_warning keys from the result body (e.g.
    // service_summary_warning). Shown above the collapsible Result section so
    // the AM sees the caveat even when Result is collapsed.
    const resultWarnings = extractResultWarnings(
      resultData as Record<string, unknown> | null,
    );
    const statusWarnings = warningsFromStatus(inst);
    const fromResult = resultLinesShown
      ? []
      : resultWarnings.length > 0
        ? resultWarnings
        : statusWarnings;
    if (!verifyWarning && fromResult.length === 0) return null;
    const all = verifyWarning ? [verifyWarning, ...fromResult] : fromResult;
    return (
      <div
        role="alert"
        className="flex flex-col gap-1 mb-1"
        data-testid="instance-warnings"
      >
        {all.map((w, i) => (
          <div
            key={i}
            className="rounded-md bg-primary-warning-50 border border-primary-warning-200 px-3 py-2"
          >
            <p className="text-xs text-primary-warning-700">{w}</p>
          </div>
        ))}
      </div>
    );
  })();

  // First-class EARLY-EXIT banner — parity with ora's WorkflowDetailDrawer.
  // A 'completed' run that quit early WITHOUT doing the work (e.g. the
  // safe-rerun guard found this change already completed in Epic and skipped
  // creating a duplicate endorsement) must say so up front, not read as a
  // clean success. Rendered ABOVE the Result hero via CompletedDrawerBody's
  // topBannerBlock slot. Tailwind (not MUI) to match this app's alert blocks.
  // Status FIRST: the substrate publishes ended_early / early_exit_reason flat
  // on the status payload (beside result_warnings), so the banner no longer
  // depends on the GET /result this drawer also makes — which can fail while
  // status still arrives. `resultData` stays as the fallback: it is the same
  // two fields one level in, and it is what older instances' payloads carry.
  const earlyExitReason =
    terminalStatus === "completed"
      ? (extractEarlyExitReason(inst as Record<string, unknown> | null) ??
        extractEarlyExitReason(resultData as Record<string, unknown> | null))
      : null;
  // email-send-complete-override, Phase 3 task 8 (AC-2 human-validation
  // half): who cleared this run, when, and why — reused from the SAME
  // resultData fetch above, never a second one. Rendered via
  // CompletedDrawerBody's manualClearBlock slot, ABOVE the Result hero.
  const manualClear =
    terminalStatus === "completed"
      ? extractManualClear(resultData as Record<string, unknown> | null)
      : null;
  const manualClearBlock = manualClear ? (
    <div
      className="rounded-md border border-primary-gray-light-200 bg-base-white px-3 py-2 mb-1 flex flex-col gap-0.5"
      data-testid="manual-clear-attribution"
    >
      <p className="text-xs text-primary-gray-light-800">
        Cleared manually by{" "}
        {manualClear.actor_upn ?? manualClear.actor_oid ?? "an AM"}
        {manualClear.cleared_at
          ? ` on ${new Date(manualClear.cleared_at).toLocaleString()}`
          : ""}
        {" — "}
        {clearDispositionLabel(manualClear.disposition)}
      </p>
      {manualClear.note && (
        <p className="text-2xs text-primary-gray-light-600">
          &ldquo;{manualClear.note}&rdquo;
        </p>
      )}
    </div>
  ) : null;

  const earlyExitBanner = earlyExitReason ? (
    <div
      role="alert"
      className="rounded-md bg-primary-warning-50 border border-primary-warning-200 px-3 py-2 mb-1"
      data-testid="early-exit-banner"
    >
      <p className="text-xs text-primary-warning-700">
        <span className="font-semibold">Ended early — </span>
        {earlyExitReason}
      </p>
    </div>
  ) : null;

  // Error block for failed / terminal-error states. Substrate errors often
  // splice a Python-dict repr into the message; extract + render it with the
  // same FormattedJsonView the Results use (fallback: raw text).
  const parsedError =
    failed && errorMessage ? extractEmbeddedJson(errorMessage) : null;
  const errorBlock =
    failed && errorMessage ? (
      <div
        role="alert"
        className="rounded-md bg-primary-error-50 border border-primary-error-200 px-3 py-2"
        data-testid="terminal-error"
      >
        <p className="text-sm font-semibold text-primary-error-700">
          Workflow failed
        </p>
        {parsedError ? (
          <>
            {parsedError.prefix && (
              <p className="text-xs text-primary-error-700 mt-1 break-words">
                {parsedError.prefix}
              </p>
            )}
            <div className="mt-2">
              <FormattedJsonView data={parsedError.data} testId="terminal-error-json" />
            </div>
            {parsedError.suffix && (
              <p className="text-xs text-primary-error-700 mt-1 break-words">
                {parsedError.suffix}
              </p>
            )}
          </>
        ) : (
          // No embedded JSON blob — Playwright / exec-plane errors are
          // multi-line (headline + "Call log:" bullets). Preserve newlines
          // with whitespace-pre-wrap so the call log stays readable instead
          // of collapsing into one flat line.
          <p className="text-xs text-primary-error-700 mt-1 whitespace-pre-wrap break-words">
            {errorMessage}
          </p>
        )}
      </div>
    ) : cancelled ? (
      <div className="rounded-md bg-primary-gray-light-50 border border-primary-gray-light-200 px-3 py-2">
        <p className="text-xs text-primary-gray-light-600">
          This run was cancelled.
        </p>
      </div>
    ) : null;

  // Plugin-specific endorsement blocks rendered as extraTopBlock
  const endorsementBlock =
    isEndorsement ? (
      <>
        {carrierHandoffData && (
          <CarrierHandoff
            url={carrierHandoffData.url}
            instructions={carrierHandoffData.instructions}
            carrierName={carrierHandoffData.carrierName}
            hasCarrierConnect={carrierHandoffData.hasCarrierConnect}
          />
        )}
        <EndorsementHistory />
      </>
    ) : null;

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col" data-testid="terminal-body">
      <div className="flex-1 min-h-0 overflow-y-auto">
      <PinnedHeader
        clientName={
          inputClientName ||
          metaClientName ||
          clientNameProp ||
          clientCtxDetails?.client.client_name ||
          null
        }
        policyNumber={inputPolicyNumber || metaPolicyNumber || null}
        rightLabel={durationLabel}
        rightLabelTitle="Duration"
        clientDetails={clientCtxDetails}
        clientLoading={clientCtxLoading}
        // A finished run is still a run somebody may need to explain, and the
        // request that started it is the first thing they will look for.
        action={
          <SourceRequestRow
            sourceMessageId={
              typeof push.status?.metadata?.graph_message_id === "string"
                ? push.status.metadata.graph_message_id
                : null
            }
            currentMessageId={openItemMessageId}
          />
        }
      />

      <div className="p-1 pt-3 flex flex-col">
        {family === "completed" ? (
          <CompletedDrawerBody
            sections={sections}
            topBannerBlock={
              ackList || (earlyExitBanner && !hasAckLine(EARLY_EXIT_WARNING_ID)) ? (
                <>
                  {ackList}
                  {hasAckLine(EARLY_EXIT_WARNING_ID) ? null : earlyExitBanner}
                </>
              ) : null
            }
            verificationWarningBlock={verificationWarningBlock}
            manualClearBlock={manualClearBlock}
            phaseStream={phaseStream}
            stepView={stepView}
            record={record}
            debugActions={debugActions}
                  moreInfoOpen={moreInfoOpen}
                  onMoreInfoClose={onMoreInfoClose}
            extraTopBlock={endorsementBlock}
          />
        ) : (
          <TerminalDrawerBody
            sections={sections}
            errorBlock={errorBlock}
            earlyExitBlock={ackList}
            phaseStream={phaseStream}
            stepView={stepView}
            record={record}
            debugActions={debugActions}
                  moreInfoOpen={moreInfoOpen}
                  onMoreInfoClose={onMoreInfoClose}
            extraTopBlock={endorsementBlock}
          />
        )}
      </div>
      </div>
    </div>
  );
}

// PluginFooterRow removed in favor of shared DrawerFooter (density="compact").
// The plugin's live + terminal modes now use the identical footer primitive
// as ora's WorkflowDetailDrawer.

